/**
 * Beautiful DOCX Exporter
 *
 * Generates professional Word documents that match the beautiful UI renderers.
 * Each section is exported with proper styling, tables, and formatting.
 */

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  HeadingLevel,
  PageBreak,
  ShadingType,
  convertInchesToTwip,
  Header,
  Footer,
  PageNumber,
  NumberFormat,
  TableOfContents,
  StyleLevel,
  LevelFormat,
  ExternalHyperlink,
  ImageRun,
  SectionType,
  TabStopType,
  PageOrientation,
} from 'docx';
import { saveAs } from 'file-saver';
import {
  buildCompanyIdentity,
  TO_BE_COMPLETED,
  type ExportCompanyIdentity,
} from '../lib/export/companyIdentity';
import { buildCoverPageModel, resolveSolicitationNumber, type CoverPageModel } from '../lib/export/coverPageModel';
import { decodeHtmlEntities, formatClosing, firstReal, stripBracketAnnotations, cleanTitle } from '../lib/export/textCleanup';
import {
  CoverLetterContent,
  ExecutiveSummaryContent,
  TechnicalApproachContent,
  ManagementApproachContent,
  PastPerformanceContent,
  PersonnelContent,
  PricingContent,
  ComplianceContent,
  isCoverLetterContent,
  isExecutiveSummaryContent,
  isTechnicalApproachContent,
  isManagementApproachContent,
  isPastPerformanceContent,
  isPersonnelContent,
  isPricingContent,
  isComplianceContent,
} from '../types/proposal-sections';
import type { DeliverablesData, ProductDefinition } from '../types/deliverables';

// ============================================================================
// COLORS (matching UI theme)
// ============================================================================

// Formal-Federal palette (BidWin_Export_Styling_Spec.md). Legacy keys are
// remapped onto the new tokens so the existing section exporters pick up the
// federal look without per-call edits.
const COLORS = {
  navy: '1F4E96',         // primary — headings, rules, table header fill
  navyDark: '173A6E',
  red: 'C8102E',          // accent
  ink: '1A1A1A',          // body text
  muted: '5A6472',        // header/footer, captions
  slate: '33415C',        // H3
  border: 'C9D3E0',       // table borders
  zebra: 'F2F5FA',        // alternating rows
  white: 'FFFFFF',

  // Legacy aliases (kept so older section code compiles + restyles automatically)
  primary: '1F4E96',
  primaryDark: '173A6E',
  success: '15722F',
  warning: '8A5A00',
  danger: 'C81E1E',
  purple: '1F4E96',       // federal theme: fold purple into navy
  gray: '33415C',
  grayLight: '5A6472',
  grayBorder: 'C9D3E0',
  blueBg: 'EAF1FA',
  greenBg: 'E6F5EC',
  purpleBg: 'F2F5FA',
  grayBg: 'F2F5FA',
};

// US Letter usable width with 1" margins = 6.5in = 9360 dxa (matches the spec).
const CONTENT_WIDTH_DXA = 9360;

// US Letter page (twips). docx defaults to A4 — must be set explicitly.
const PAGE_SIZE_LETTER = { width: 12240, height: 15840 };

// Font sizes (half-points). Body prose must be >= 24 (12pt) per Section E.
// Tables and running furniture are exempt and stay smaller.
const TBL_HDR_SIZE = 20;   // 10pt table header (white on navy)
const TBL_CELL_SIZE = 22;  // 11pt table body
const FURN_SIZE = 18;      // 9pt running header
// 8pt footer stays a literal (16); cover uses deliberate display sizes.

// Real Word list formatting (navy bullet glyph + hanging indent) — never a typed
// "•" character in body text (spec requirement). Attached to every Document.
const BULLET_REF = 'bw-bullets';
const BULLET_NUMBERING = {
  config: [
    {
      reference: BULLET_REF,
      levels: [
        {
          level: 0,
          format: LevelFormat.BULLET,
          text: '•',
          alignment: AlignmentType.LEFT,
          style: { run: { color: COLORS.navy }, paragraph: { indent: { left: convertInchesToTwip(0.4), hanging: convertInchesToTwip(0.2) } } },
        },
        {
          level: 1,
          format: LevelFormat.BULLET,
          text: '◦',
          alignment: AlignmentType.LEFT,
          style: { run: { color: COLORS.navy }, paragraph: { indent: { left: convertInchesToTwip(0.7), hanging: convertInchesToTwip(0.2) } } },
        },
      ],
    },
  ],
};

// ============================================================================
// INTERFACE
// ============================================================================

interface Contract {
  title: string;
  agency: string;
  notice_id?: string;
  id?: string;
}

interface ProposalContent {
  coverLetter: string;
  demoLink?: string;
  technicalSections: Array<{
    title: string;
    content: string;
  }>;
  feedback?: string;
  contractAnalysis?: string;
  profileAnalysis?: string;
  message?: string;
  aiFeedback?: any;
  deliverablesData?: DeliverablesData;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function parseContent(content: string): any {
  if (!content) return null;
  if (typeof content === 'object') return content;
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
}

function stripCitations(text: unknown): string {
  // Be tolerant of non-string values. Enhanced/regenerated sections sometimes
  // return arrays or objects where a string is expected; coerce them to sane
  // text instead of throwing (text.replace is not a function).
  const s = toPlainText(text);
  if (!s) return '';
  // Submission-ready: drop ALL inline bracket annotations (citations, evidence
  // tags, [DATA NEEDED] gap markers) and tidy whitespace.
  return stripBracketAnnotations(s).replace(/\s+/g, ' ').trim();
}

// Coerce any value into readable plain text without ever throwing.
function toPlainText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return decodeHtmlEntities(v);
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(toPlainText).filter(Boolean).join('\n');
  if (typeof v === 'object') {
    // Join scalar values of the object (skip nested objects to avoid noise);
    // fall back to JSON so nothing is silently lost.
    const parts = Object.values(v as Record<string, unknown>)
      .map((x) => (x != null && typeof x !== 'object' ? String(x) : ''))
      .filter(Boolean);
    if (parts.length) return parts.join(' ');
    try { return JSON.stringify(v); } catch { return ''; }
  }
  return String(v);
}

function createSectionHeading(text: string, level: 1 | 2 | 3 = 1): Paragraph {
  const colors: Record<number, string> = {
    1: COLORS.navy,
    2: COLORS.navy,
    3: COLORS.slate,
  };
  const sizes: Record<number, number> = {
    1: 30,  // 15pt
    2: 26,  // 13pt
    3: 24,  // 12pt
  };

  // Make subsection headings SEMANTIC (real Heading 2/3) so the document has a
  // navigable outline and the TOC/PDF bookmarks populate — not visual-only bold.
  const headingLevel = level === 3 ? HeadingLevel.HEADING_3 : HeadingLevel.HEADING_2;

  return new Paragraph({
    heading: headingLevel,
    children: [
      new TextRun({
        text,
        bold: true,
        size: sizes[level],
        color: colors[level],
      }),
    ],
    spacing: { before: 240, after: level === 1 ? 120 : 80 },
  });
}

function createParagraph(text: string, options?: { indent?: number; spacing?: number }): Paragraph {
  return new Paragraph({
    children: [new TextRun({ text: stripCitations(text), size: 24, color: COLORS.ink })],
    alignment: AlignmentType.JUSTIFIED,
    spacing: { after: options?.spacing ?? 160, line: 276 },
    indent: options?.indent ? { left: convertInchesToTwip(options.indent * 0.25) } : undefined,
  });
}

function createBulletPoint(text: string, indent: number = 0): Paragraph {
  return new Paragraph({
    numbering: { reference: BULLET_REF, level: Math.min(indent, 1) },
    children: [new TextRun({ text: stripCitations(text), size: 24, color: COLORS.ink })],
    spacing: { after: 80, line: 276 },
  });
}

function createLabeledField(label: string, value: string): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({ text: `${label}: `, bold: true, size: 24, color: COLORS.ink }),
      new TextRun({ text: stripCitations(value), size: 24, color: COLORS.ink }),
    ],
    spacing: { after: 80, line: 276 },
  });
}

function createStyledTable(
  headers: string[],
  rows: string[][],
  options?: { headerBgColor?: string; alternateRowColors?: boolean; firstColumnBold?: boolean }
): Table {
  // Spec: table header is ALWAYS navy fill with white bold text. Ignore any
  // legacy light headerBgColor (those produced invisible white-on-light text).
  const headerBg = COLORS.navy;
  void options?.headerBgColor;
  const alternateRows = options?.alternateRowColors !== false; // Default true
  const firstColBold = options?.firstColumnBold !== false; // Default true

  const cellBorder = { style: BorderStyle.SINGLE, size: 6, color: COLORS.border };
  const cellMargins = { top: 70, bottom: 70, left: 110, right: 110 };

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: cellBorder, bottom: cellBorder, left: cellBorder, right: cellBorder,
      insideHorizontal: cellBorder, insideVertical: cellBorder,
    },
    rows: [
      // Header row — navy fill, white bold 10pt
      new TableRow({
        tableHeader: true,
        children: headers.map(header =>
          new TableCell({
            margins: cellMargins,
            children: [new Paragraph({
              children: [new TextRun({ text: header, bold: true, size: TBL_HDR_SIZE, color: COLORS.white })],
              spacing: { before: 20, after: 20 },
            })],
            shading: { fill: headerBg, type: ShadingType.CLEAR, color: 'auto' },
          })
        ),
      }),
      // Data rows — 11pt, zebra shading on even rows
      ...rows.map((row, rowIdx) =>
        new TableRow({
          children: row.map((cell, colIdx) =>
            new TableCell({
              margins: cellMargins,
              children: [new Paragraph({
                children: [new TextRun({
                  text: stripCitations(cell),
                  size: TBL_CELL_SIZE,
                  color: COLORS.ink,
                  bold: firstColBold && colIdx === 0,
                })],
                spacing: { before: 20, after: 20, line: 252 },
              })],
              shading: alternateRows && rowIdx % 2 === 1
                ? { fill: COLORS.zebra, type: ShadingType.CLEAR, color: 'auto' }
                : undefined,
            })
          ),
        })
      ),
    ],
  });
}

/** A real horizontal rule (paragraph bottom border) — never typed dashes. */
function createRule(color: string = COLORS.border, opts?: { before?: number; after?: number; size?: number }): Paragraph {
  return new Paragraph({
    children: [],
    border: { bottom: { style: BorderStyle.SINGLE, size: opts?.size ?? 6, color } },
    spacing: { before: opts?.before ?? 120, after: opts?.after ?? 160 },
  });
}

// Track section numbers for the document
let sectionCounter = 0;

function resetSectionCounter(): void {
  sectionCounter = 0;
}

function createNumberedSectionHeading(text: string, level: 1 | 2 | 3 = 1): Paragraph {
  const colors: Record<number, string> = {
    1: COLORS.primary,
    2: COLORS.primaryDark,
    3: COLORS.gray,
  };
  const sizes: Record<number, number> = {
    1: 36,  // 18pt
    2: 28,  // 14pt
    3: 24,  // 12pt
  };

  // Only increment for level 1 headings
  if (level === 1) {
    sectionCounter++;
  }

  const sectionNum = level === 1 ? `${sectionCounter}.0  ` : '';

  return new Paragraph({
    children: [
      new TextRun({
        text: sectionNum + text,
        bold: true,
        size: sizes[level],
        color: colors[level],
      }),
    ],
    heading: level === 1 ? HeadingLevel.HEADING_1 : level === 2 ? HeadingLevel.HEADING_2 : HeadingLevel.HEADING_3,
    spacing: { before: level === 1 ? 400 : 300, after: 200 },
    border: level === 1 ? {
      bottom: { style: BorderStyle.SINGLE, size: 12, color: COLORS.primary },
    } : undefined,
  });
}

function createHighlightBox(
  title: string,
  content: string | Paragraph[],
  options?: { bgColor?: string; borderColor?: string; number?: number }
): Paragraph[] {
  const elements: Paragraph[] = [];

  // Title with optional number
  const titleText = options?.number ? `${options.number}. ${title}` : title;
  elements.push(new Paragraph({
    children: [new TextRun({ text: titleText, bold: true, size: 24, color: COLORS.gray })],
    spacing: { before: 200, after: 100 },
    shading: options?.bgColor ? { fill: options.bgColor, type: ShadingType.CLEAR } : undefined,
    border: options?.borderColor ? {
      left: { style: BorderStyle.SINGLE, size: 24, color: options.borderColor },
    } : undefined,
  }));

  // Content
  if (typeof content === 'string') {
    elements.push(new Paragraph({
      children: [new TextRun({ text: stripCitations(content), size: 24})],
      spacing: { after: 150 },
      indent: { left: convertInchesToTwip(0.1) },
    }));
  } else {
    elements.push(...content);
  }

  return elements;
}

// ============================================================================
// COVER LETTER EXPORTER
// ============================================================================

function exportCoverLetter(data: CoverLetterContent, identity?: ExportCompanyIdentity, demoLink?: string): Paragraph[] {
  const elements: Paragraph[] = [];

  // Company Header
  elements.push(new Paragraph({
    children: [new TextRun({ text: data.companyInfo.name, bold: true, size: 28, color: COLORS.navy })],
    alignment: AlignmentType.LEFT,
    spacing: { after: 80 },
  }));

  elements.push(createParagraph(data.companyInfo.address));
  elements.push(createParagraph(data.companyInfo.cityStateZip));

  // Contact info
  elements.push(new Paragraph({
    children: [
      new TextRun({ text: `Phone: ${data.companyInfo.phone}`, size: 24 }),
      new TextRun({ text: '  |  ', size: 24 }),
      new TextRun({ text: `Email: ${data.companyInfo.email}`, size: 24 }),
      ...(data.companyInfo.website ? [
        new TextRun({ text: '  |  ', size: 24 }),
        new TextRun({ text: data.companyInfo.website, size: 24 }),
      ] : []),
    ],
    spacing: { after: 300 },
  }));

  // Horizontal rule (real border)
  elements.push(createRule(COLORS.navy, { before: 60, after: 200, size: 8 }));

  // Date
  elements.push(createParagraph(data.date, { spacing: 200 }));

  // Via Electronic Submission
  elements.push(new Paragraph({
    children: [new TextRun({ text: 'VIA ELECTRONIC SUBMISSION', bold: true, size: 24})],
    spacing: { after: 200 },
  }));

  // Recipient
  if (data.recipient.name) {
    elements.push(new Paragraph({
      children: [new TextRun({ text: data.recipient.name, bold: true, size: 24})],
    }));
  }
  elements.push(createParagraph(data.recipient.title));
  if (data.recipient.office) {
    elements.push(createParagraph(data.recipient.office));
  }
  elements.push(createParagraph(data.recipient.agency));
  if (data.recipient.address) {
    String(data.recipient.address)
      .split('\n')
      .map((l: string) => l.trim())
      .filter(Boolean)
      .forEach((line: string) => elements.push(createParagraph(line)));
  }
  elements.push(new Paragraph({ children: [], spacing: { after: 200 } }));

  // Subject Box
  elements.push(new Paragraph({
    children: [
      new TextRun({ text: 'RE: ', bold: true, size: 24}),
      new TextRun({ text: data.subject.contractTitle, bold: true, size: 24}),
    ],
    shading: { fill: COLORS.grayBg, type: ShadingType.CLEAR },
    spacing: { after: 80 },
  }));

  if (data.subject.solicitationNumber) {
    elements.push(new Paragraph({
      children: [
        new TextRun({ text: 'Solicitation Number: ', bold: true, size: 24}),
        new TextRun({ text: data.subject.solicitationNumber, size: 24}),
      ],
      shading: { fill: COLORS.grayBg, type: ShadingType.CLEAR },
    }));
  }

  if (data.subject.naicsCode) {
    elements.push(new Paragraph({
      children: [
        new TextRun({ text: 'NAICS Code: ', bold: true, size: 24}),
        new TextRun({ text: data.subject.naicsCode, size: 24}),
      ],
      shading: { fill: COLORS.grayBg, type: ShadingType.CLEAR },
      spacing: { after: 200 },
    }));
  }

  // Salutation
  elements.push(new Paragraph({
    children: [new TextRun({
      text: data.recipient.salutation
        || `Dear ${data.recipient.name || data.recipient.title || 'Contracting Officer'}:`,
      size: 24,
    })],
    spacing: { before: 200, after: 200 },
  }));

  // Body paragraphs
  elements.push(createParagraph(data.body.opening, { spacing: 200 }));
  elements.push(createParagraph(data.body.qualifications, { spacing: 200 }));
  elements.push(createParagraph(data.body.valueProposition, { spacing: 200 }));
  elements.push(createParagraph(data.body.understanding, { spacing: 200 }));
  elements.push(createParagraph(data.body.closing, { spacing: 200 }));

  // Signature — closing without doubled punctuation; fall back to the offeror
  // identity (and never print [AUTHORIZED REPRESENTATIVE]/[TITLE] placeholders).
  const closing = formatClosing(data.signature?.salutation) || 'Respectfully submitted,';
  const signerName = firstReal(data.signature?.name, identity?.pocName);
  const signerTitle = firstReal(data.signature?.title, identity?.pocTitle);
  const signerCompany = firstReal(data.signature?.company, identity?.companyName, data.companyInfo?.name);

  elements.push(new Paragraph({
    children: [new TextRun({ text: closing, size: 24, color: COLORS.ink })],
    spacing: { before: 300, after: 500 },
  }));

  if (signerName) {
    elements.push(new Paragraph({
      children: [new TextRun({ text: signerName, bold: true, size: 24, color: COLORS.ink })],
    }));
  }
  if (signerTitle) elements.push(createParagraph(signerTitle));
  if (signerCompany) elements.push(createParagraph(signerCompany, { spacing: 200 }));

  // Enclosures
  if (data.enclosures && data.enclosures.length > 0) {
    elements.push(createRule(COLORS.border, { before: 200, after: 100 }));

    elements.push(new Paragraph({
      children: [new TextRun({ text: 'Enclosures:', bold: true, size: 24})],
      spacing: { after: 80 },
    }));

    data.enclosures.forEach(enc => {
      elements.push(createBulletPoint(enc));
    });
  }

  // Live Demonstration link (e.g. a deployed demo accompanying the submission).
  if (demoLink && demoLink.trim()) {
    elements.push(createRule(COLORS.border, { before: 200, after: 100 }));
    elements.push(new Paragraph({
      children: [new TextRun({ text: 'Live Demonstration:', bold: true, size: 24 })],
      spacing: { after: 60 },
    }));
    elements.push(new Paragraph({
      children: [
        new ExternalHyperlink({
          children: [new TextRun({ text: demoLink.trim(), underline: {}, color: COLORS.navy, size: 24 })],
          link: demoLink.trim(),
        }),
      ],
      spacing: { after: 120 },
    }));
  }

  return elements;
}

// ============================================================================
// EXECUTIVE SUMMARY EXPORTER
// ============================================================================

function exportExecutiveSummary(data: ExecutiveSummaryContent): Paragraph[] {
  const elements: Paragraph[] = [];

  // Value Proposition
  elements.push(createSectionHeading('Value Proposition', 2));
  elements.push(createParagraph(data.valueProposition));

  // Understanding of Requirements
  elements.push(createSectionHeading('Understanding of Requirements', 2));
  elements.push(createParagraph(data.understandingOfRequirements));

  // Key Discriminators
  elements.push(createSectionHeading('Key Discriminators', 2));
  data.keyDiscriminators.forEach((disc, idx) => {
    elements.push(...createHighlightBox(disc.title, disc.description, {
      number: idx + 1,
      borderColor: COLORS.primary,
    }));
  });

  // Relevant Experience Table
  if (data.relevantExperience && data.relevantExperience.length > 0) {
    elements.push(createSectionHeading('Relevant Experience', 2));
    elements.push(createStyledTable(
      ['Project', 'Client', 'Value', 'Relevance'],
      data.relevantExperience.map(exp => [exp.project, exp.client, exp.value, exp.relevance])
    ));
  }

  // Technical Overview
  elements.push(createSectionHeading('Technical Solution Overview', 2));
  elements.push(createParagraph(data.technicalOverview));

  // Management Overview
  elements.push(createSectionHeading('Management Approach Summary', 2));
  elements.push(createParagraph(data.managementOverview));

  // Conclusion
  elements.push(createSectionHeading('Conclusion', 2));
  elements.push(new Paragraph({
    children: [new TextRun({ text: stripCitations(data.conclusion), size: 24})],
    shading: { fill: COLORS.grayBg, type: ShadingType.CLEAR },
    spacing: { after: 200 },
  }));

  return elements;
}

// ============================================================================
// TECHNICAL APPROACH EXPORTER
// ============================================================================

function exportTechnicalApproach(data: TechnicalApproachContent): Paragraph[] {
  const elements: Paragraph[] = [];

  // Scope Overview
  elements.push(createSectionHeading('Scope Overview', 2));
  elements.push(createParagraph(data.scopeOverview));

  // Technical Challenges
  if (data.technicalChallenges) {
    elements.push(createSectionHeading('Technical Challenges & Approach', 2));
    elements.push(createParagraph(data.technicalChallenges));
  }

  // Methodology
  if (data.methodology) {
    elements.push(createSectionHeading('Technical Methodology', 2));
    elements.push(createParagraph(data.methodology.overallApproach));

    if (data.methodology.tasks && data.methodology.tasks.length > 0) {
      elements.push(createSectionHeading('Task Breakdown', 3));

      data.methodology.tasks.forEach((task, idx) => {
        elements.push(...createHighlightBox(
          `Task ${idx + 1}: ${task.name}`,
          [
            createParagraph(task.approach),
            ...(task.tools?.length ? [createLabeledField('Tools', task.tools.join(', '))] : []),
            ...(task.deliverables?.length ? [createLabeledField('Deliverables', task.deliverables.join(', '))] : []),
          ],
          { borderColor: COLORS.primary }
        ));
      });
    }
  }

  // Tools & Technologies
  if (data.toolsAndTechnologies && data.toolsAndTechnologies.length > 0) {
    elements.push(createSectionHeading('Tools & Technologies', 2));
    elements.push(createStyledTable(
      ['Category', 'Technology', 'Purpose'],
      data.toolsAndTechnologies.map(t => [t.category, t.technology, t.purpose])
    ));
  }

  // Innovation
  if (data.innovation) {
    elements.push(createSectionHeading('Innovation & Value-Added Features', 2));
    elements.push(new Paragraph({
      children: [new TextRun({ text: stripCitations(data.innovation), size: 24})],
      shading: { fill: COLORS.purpleBg, type: ShadingType.CLEAR },
      spacing: { after: 200 },
    }));
  }

  // Quality Assurance
  if (data.qualityAssurance) {
    elements.push(createSectionHeading('Quality Assurance', 2));
    elements.push(createParagraph(data.qualityAssurance));
  }

  // Schedule
  if (data.schedule && data.schedule.length > 0) {
    elements.push(createSectionHeading('Implementation Schedule', 2));
    elements.push(createStyledTable(
      ['Phase', 'Duration', 'Milestones', 'Deliverables'],
      data.schedule.map(s => [s.phase, s.duration, s.milestones, s.deliverables])
    ));
  }

  return elements;
}

// ============================================================================
// MANAGEMENT APPROACH EXPORTER
// ============================================================================

function exportManagementApproach(data: ManagementApproachContent): Paragraph[] {
  const elements: Paragraph[] = [];

  // Corporate Organization
  if (data.corporateOrganization) {
    elements.push(createSectionHeading('Corporate Organization', 2));
    elements.push(createParagraph(data.corporateOrganization));
  }

  // Organization Chart
  if (data.organizationChart) {
    elements.push(createSectionHeading('Organization Structure', 2));
    elements.push(new Paragraph({
      children: [new TextRun({ text: data.organizationChart, size: 24, font: 'Times New Roman' })],
      shading: { fill: COLORS.grayBg, type: ShadingType.CLEAR },
      spacing: { after: 200 },
    }));
  }

  // Key Roles
  if (data.keyRoles && data.keyRoles.length > 0) {
    elements.push(createSectionHeading('Key Management Roles', 2));

    data.keyRoles.forEach(role => {
      const roleName = role.name ? `${role.role} - ${role.name}` : role.role;

      elements.push(new Paragraph({
        children: [new TextRun({ text: roleName, bold: true, size: 24, color: COLORS.gray })],
        spacing: { before: 200, after: 80 },
      }));

      elements.push(new Paragraph({
        children: [new TextRun({ text: role.qualifications, size: 24, color: COLORS.grayLight })],
        spacing: { after: 80 },
      }));

      if (role.responsibilities && role.responsibilities.length > 0) {
        elements.push(new Paragraph({
          children: [new TextRun({ text: 'Responsibilities:', bold: true, size: 24 })],
          spacing: { after: 40 },
        }));
        role.responsibilities.forEach(resp => {
          elements.push(createBulletPoint(resp, 1));
        });
      }
    });
  }

  // Communication Plan
  if (data.communicationPlan) {
    elements.push(createSectionHeading('Communication Plan', 2));
    elements.push(createParagraph(data.communicationPlan.reportingStructure));

    if (data.communicationPlan.meetings && data.communicationPlan.meetings.length > 0) {
      elements.push(createStyledTable(
        ['Meeting Type', 'Frequency', 'Attendees', 'Purpose'],
        data.communicationPlan.meetings.map(m => [m.type, m.frequency, m.attendees, m.purpose])
      ));
    }
  }

  // Quality Management
  if (data.qualityManagement) {
    elements.push(createSectionHeading('Quality Management', 2));
    elements.push(createParagraph(data.qualityManagement.process));

    if (data.qualityManagement.metrics && data.qualityManagement.metrics.length > 0) {
      elements.push(createStyledTable(
        ['Metric', 'Target', 'Measurement Method'],
        data.qualityManagement.metrics.map(m => [m.metric, m.target, m.method])
      ));
    }
  }

  // Risk Management
  if (data.riskManagement && data.riskManagement.length > 0) {
    elements.push(createSectionHeading('Risk Management', 2));
    elements.push(createStyledTable(
      ['Risk', 'Likelihood', 'Impact', 'Mitigation'],
      data.riskManagement.map(r => [r.risk, r.likelihood, r.impact, r.mitigation])
    ));
  }

  // Transition Plan
  if (data.transitionPlan) {
    elements.push(createSectionHeading('Transition Plan', 2));
    elements.push(new Paragraph({
      children: [new TextRun({ text: stripCitations(data.transitionPlan), size: 24})],
      shading: { fill: COLORS.blueBg, type: ShadingType.CLEAR },
      spacing: { after: 200 },
    }));
  }

  return elements;
}

// ============================================================================
// PAST PERFORMANCE EXPORTER
// ============================================================================

function exportPastPerformance(data: PastPerformanceContent): Paragraph[] {
  const elements: Paragraph[] = [];

  // Summary
  elements.push(createSectionHeading('Summary of Relevant Experience', 2));
  elements.push(createParagraph(data.summary));

  // Contracts
  elements.push(createSectionHeading('Past Performance Narratives', 2));

  data.contracts.forEach(contract => {
    // Contract header
    elements.push(new Paragraph({
      children: [
        new TextRun({ text: contract.name, bold: true, size: 26, color: COLORS.gray }),
      ],
      spacing: { before: 300, after: 80 },
      border: {
        bottom: { style: BorderStyle.SINGLE, size: 8, color: COLORS.grayBorder },
      },
    }));

    elements.push(new Paragraph({
      children: [new TextRun({ text: contract.client, size: 24, color: COLORS.grayLight })],
      spacing: { after: 80 },
    }));

    // Contract details
    if (contract.contractNumber) {
      elements.push(createLabeledField('Contract #', contract.contractNumber));
    }
    elements.push(createLabeledField('Value', contract.value));
    elements.push(createLabeledField('Period', contract.period));
    if (contract.contractType) {
      elements.push(createLabeledField('Type', contract.contractType));
    }

    // Scope
    elements.push(new Paragraph({
      children: [new TextRun({ text: 'Scope of Work', bold: true, size: 24})],
      spacing: { before: 150, after: 80 },
    }));
    elements.push(createParagraph(contract.scopeOfWork));

    // Relevance
    elements.push(new Paragraph({
      children: [new TextRun({ text: 'Relevance to Current Requirement', bold: true, size: 24})],
      spacing: { before: 150, after: 80 },
    }));
    elements.push(createParagraph(contract.relevance));

    // Achievements
    if (contract.achievements && contract.achievements.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Key Achievements', bold: true, size: 24})],
        spacing: { before: 150, after: 80 },
      }));
      contract.achievements.forEach(a => {
        elements.push(createBulletPoint(a));
      });
    }

    // Reference
    if (contract.reference) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Reference', bold: true, size: 24})],
        spacing: { before: 150, after: 80 },
      }));
      elements.push(createParagraph(contract.reference.name));
      elements.push(createParagraph(contract.reference.title));
      if (contract.reference.phone) elements.push(createParagraph(contract.reference.phone));
      if (contract.reference.email) elements.push(createParagraph(contract.reference.email));
    }
  });

  // Relevance Summary
  elements.push(createSectionHeading('Relevance Summary', 2));
  elements.push(new Paragraph({
    children: [new TextRun({ text: stripCitations(data.relevanceSummary), size: 24})],
    shading: { fill: COLORS.blueBg, type: ShadingType.CLEAR },
    spacing: { after: 200 },
  }));

  return elements;
}

// ============================================================================
// PERSONNEL EXPORTER
// ============================================================================

function exportPersonnel(data: PersonnelContent): Paragraph[] {
  const elements: Paragraph[] = [];

  // Summary
  if (data.summary) {
    elements.push(createSectionHeading('Personnel Summary', 2));
    elements.push(createParagraph(data.summary));
  }

  // Key Personnel
  if (data.keyPersonnel && data.keyPersonnel.length > 0) {
    elements.push(createSectionHeading('Key Personnel', 2));

    data.keyPersonnel.forEach(person => {
      // Person header
      elements.push(new Paragraph({
        children: [new TextRun({ text: person.name, bold: true, size: 26, color: COLORS.gray })],
        spacing: { before: 300, after: 40 },
      }));

      elements.push(new Paragraph({
        children: [new TextRun({ text: person.role, bold: true, size: 24, color: COLORS.primary })],
        spacing: { after: 40 },
      }));

      const experienceText = `${person.yearsExperience}+ years experience${person.clearance ? ` • ${person.clearance} Clearance` : ''}`;
      elements.push(new Paragraph({
        children: [new TextRun({ text: experienceText, size: 24, color: COLORS.grayLight })],
        spacing: { after: 120 },
      }));

      // Certifications
      if (person.certifications && person.certifications.length > 0) {
        elements.push(new Paragraph({
          children: [new TextRun({ text: 'Certifications', bold: true, size: 24 })],
          spacing: { after: 60 },
        }));
        elements.push(new Paragraph({
          children: [new TextRun({ text: person.certifications.join(' | '), size: 24, color: COLORS.success })],
          spacing: { after: 100 },
        }));
      }

      // Education
      if (person.education && person.education.length > 0) {
        elements.push(new Paragraph({
          children: [new TextRun({ text: 'Education', bold: true, size: 24 })],
          spacing: { after: 60 },
        }));
        person.education.forEach(edu => {
          elements.push(createBulletPoint(edu));
        });
      }

      // Relevant Experience
      if (person.relevantExperience && person.relevantExperience.length > 0) {
        elements.push(new Paragraph({
          children: [new TextRun({ text: 'Relevant Experience', bold: true, size: 24 })],
          spacing: { before: 120, after: 60 },
        }));

        person.relevantExperience.forEach(exp => {
          elements.push(new Paragraph({
            children: [new TextRun({ text: exp.position, bold: true, size: 24 })],
            spacing: { after: 20 },
            indent: { left: convertInchesToTwip(0.25) },
            border: { left: { style: BorderStyle.SINGLE, size: 8, color: COLORS.grayBorder } },
          }));
          elements.push(new Paragraph({
            children: [new TextRun({ text: `${exp.organization} • ${exp.years}`, size: 24, color: COLORS.grayLight })],
            spacing: { after: 60 },
            indent: { left: convertInchesToTwip(0.25) },
          }));
          if (exp.responsibilities && exp.responsibilities.length > 0) {
            exp.responsibilities.forEach(resp => {
              elements.push(createBulletPoint(resp, 1));
            });
          }
        });
      }
    });
  }

  // Staffing Plan
  if (data.staffingPlan && data.staffingPlan.length > 0) {
    elements.push(createSectionHeading('Staffing Plan', 2));
    elements.push(createStyledTable(
      ['Labor Category', 'Quantity', 'FTE', 'Location'],
      data.staffingPlan.map(s => [s.category, String(s.quantity), s.fte, s.location])
    ));
  }

  // Recruitment & Substitution
  if (data.recruitmentApproach) {
    elements.push(createSectionHeading('Recruitment & Retention', 3));
    elements.push(createParagraph(data.recruitmentApproach));
  }

  if (data.substitutionProcess) {
    elements.push(createSectionHeading('Personnel Substitution', 3));
    elements.push(createParagraph(data.substitutionProcess));
  }

  return elements;
}

// ============================================================================
// PRICING EXPORTER (Enhanced for Federal Compliance)
// ============================================================================

function exportPricing(data: PricingContent): (Paragraph | Table)[] {
  const elements: (Paragraph | Table)[] = [];

  // Cast to any for extended fields from Federal Compliance view
  const pricingData = data as any;

  // ==================== FAR TABLE 15-2 COVER PAGE ====================
  if (pricingData.farTable15_2CoverPage) {
    const cover = pricingData.farTable15_2CoverPage;
    elements.push(createSectionHeading('Cost/Price Proposal Cover Sheet (FAR 15-2)', 2));

    const coverRows: string[][] = [
      ['Solicitation Number', cover.solicitationNumber || '[TO BE COMPLETED]'],
      ['Offeror', cover.offerorInfo?.name || '[COMPANY NAME]'],
      ['Address', cover.offerorInfo?.address || '[ADDRESS]'],
      ['CAGE Code', cover.offerorInfo?.cageCode || 'XXXXX'],
      ['DUNS Number', cover.offerorInfo?.dunsNumber || 'XX-XXX-XXXX'],
      ['Contract Type', cover.contractType || 'FFP'],
      ['Proposal Validity', cover.proposalValidityPeriod || 'Valid for 120 days'],
    ];

    if (cover.proposedCostSummary) {
      coverRows.push(['Total Cost', cover.proposedCostSummary.totalCost || '$X,XXX,XXX']);
      coverRows.push(['Total Profit/Fee', cover.proposedCostSummary.totalProfit || '$XXX,XXX']);
      coverRows.push(['TOTAL PRICE', cover.proposedCostSummary.totalPrice || '$X,XXX,XXX']);
    }

    elements.push(createStyledTable(['Field', 'Value'], coverRows, { headerBgColor: COLORS.grayBg }));

    if (cover.certificationStatement) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: cover.certificationStatement, size: 24, italics: true })],
        spacing: { before: 150, after: 200 },
      }));
    }
  }

  // Commercial (FAR 12/13) buys must NOT show the FAR Table 15-2 cost apparatus —
  // mirrors the on-screen PricingRenderer's isCommercialPricing check exactly, so
  // Word shows the same artifact set as the app/PDF.
  const isCommercialPricing =
    pricingData.pricingMode?.commercial === true ||
    pricingData.farTable15_2CoverPage?.applicable === false ||
    pricingData.clinPriceSchedule?.applicable === true;

  // ==================== PRICE CHECK VS. SOLICITATION FLOOR/CEILING ====================
  if (pricingData.priceValidation?.warnings?.length > 0) {
    elements.push(createSectionHeading('Price Check vs. Solicitation', 2));
    pricingData.priceValidation.warnings.forEach((w: string) => {
      elements.push(new Paragraph({
        children: [new TextRun({ text: stripCitations(w), size: 24, color: COLORS.warning })],
        shading: { fill: 'FFF8EC', type: ShadingType.CLEAR },
        border: { left: { style: BorderStyle.SINGLE, size: 24, color: COLORS.warning } },
        spacing: { after: 100 },
        indent: { left: convertInchesToTwip(0.1) },
      }));
    });
    if (pricingData.priceValidation.reconciliation?.guidance) {
      elements.push(createParagraph(pricingData.priceValidation.reconciliation.guidance));
    }
  }

  // ==================== COMMERCIAL ACQUISITION BANNER ====================
  if (isCommercialPricing) {
    elements.push(new Paragraph({
      children: [new TextRun({
        text: `Commercial Acquisition (FAR Part ${pricingData.pricingMode?.farPart || '12/13'})`,
        bold: true, size: 24, color: COLORS.primary,
      })],
      shading: { fill: COLORS.blueBg, type: ShadingType.CLEAR },
      spacing: { before: 150, after: 40 },
    }));
    elements.push(new Paragraph({
      children: [new TextRun({
        text: (pricingData.pricingMode?.note ||
          'Pricing is presented as firm unit and total prices per CLIN. No FAR Table 15-2 cost buildup or certified cost-or-pricing data is required.')
          + (pricingData.pricingMode?.instrument ? ` Submit on: ${pricingData.pricingMode.instrument}.` : ''),
        size: 24, color: COLORS.primary,
      })],
      shading: { fill: COLORS.blueBg, type: ShadingType.CLEAR },
      spacing: { after: 200 },
    }));
  }

  // ==================== CLIN PRICE SCHEDULE (the artifact the solicitation asks for) ====================
  if (isCommercialPricing && pricingData.clinPriceSchedule?.clins?.length > 0) {
    elements.push(createSectionHeading('CLIN Price Schedule', 2));
    if (pricingData.clinPriceSchedule.source) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: pricingData.clinPriceSchedule.source, italics: true, size: 22, color: COLORS.grayLight })],
        spacing: { after: 80 },
      }));
    }
    const scheduleRows = pricingData.clinPriceSchedule.clins.map((c: any) => [
      c.clin || '—',
      c.description || '—',
      c.unit || '—',
      c.quantityNote || '—',
      c.unitPrice || '—',
      c.totalPrice || '—',
    ]);
    elements.push(createStyledTable(
      ['CLIN', 'Description', 'Unit', 'Qty', 'Unit Price', 'Total Price'],
      scheduleRows
    ));
    if (pricingData.clinPriceSchedule.instructions) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: pricingData.clinPriceSchedule.instructions, italics: true, size: 22, color: COLORS.grayLight })],
        spacing: { before: 60, after: 150 },
      }));
    }
  }

  // ==================== DETAILED LINE ITEMS ====================
  if (pricingData.lineItemDetails?.length > 0) {
    elements.push(createSectionHeading('Detailed Line Items', 2));
    const lineRows = pricingData.lineItemDetails.map((item: any) => [
      item.item || '—',
      item.description || item.purpose || item.justification || '—',
      item.quantity != null ? `${item.quantity}${item.unit ? ` ${item.unit}` : ''}` : '—',
      item.unitRate || '—',
      item.loadedRate || '—',
      item.totalCost || item.amount || '—',
    ]);
    elements.push(createStyledTable(
      ['Item', 'Description', 'Qty', 'Base $/hr', 'Loaded $/hr', 'Total'],
      lineRows
    ));
  }

  // ==================== CONTRACT TYPE RECOMMENDATION ====================
  if (data.contractTypeRecommendation) {
    elements.push(createSectionHeading('Contract Type Recommendation', 2));
    elements.push(new Paragraph({
      children: [
        new TextRun({ text: data.contractTypeRecommendation.recommended, bold: true, size: 32, color: COLORS.primary }),
      ],
      shading: { fill: COLORS.blueBg, type: ShadingType.CLEAR },
      spacing: { after: 80 },
    }));
    elements.push(new Paragraph({
      children: [new TextRun({ text: data.contractTypeRecommendation.rationale, size: 24})],
      shading: { fill: COLORS.blueBg, type: ShadingType.CLEAR },
      spacing: { after: 200 },
    }));
  }

  // ==================== CLIN STRUCTURE TABLE ====================
  if (pricingData.clinStructure && pricingData.clinStructure.length > 0) {
    elements.push(createSectionHeading('Contract Line Item Number (CLIN) Structure', 2));

    const clinRows = pricingData.clinStructure.map((clin: any) => [
      clin.clinNumber || '',
      clin.description || '',
      clin.contractType || 'FFP',
      String(clin.quantity || ''),
      clin.unit || '',
      clin.unitPrice || '',
      clin.totalPrice || '',
    ]);

    elements.push(createStyledTable(
      ['CLIN', 'Description', 'Type', 'Qty', 'Unit', 'Unit Price', 'Total Price'],
      clinRows,
      { headerBgColor: COLORS.blueBg }
    ));
  }

  // ==================== LABOR HOURS SUMMARY ====================
  if (pricingData.laborHoursSummary?.rows?.length > 0) {
    elements.push(createSectionHeading('Labor Hours Summary', 2));

    const headers = pricingData.laborHoursSummary.headers || ['Category', 'Year 1', 'Year 2', 'Year 3', 'Total'];
    const rows = pricingData.laborHoursSummary.rows.map((row: any) => [
      row.category || '',
      ...(row.hours || []).map((h: number) => h?.toLocaleString() || '0'),
    ]);

    elements.push(createStyledTable(headers, rows));

    if (pricingData.laborHoursSummary.fteEquivalent) {
      elements.push(new Paragraph({
        children: [
          new TextRun({ text: 'FTE Equivalent: ', bold: true, size: 24 }),
          new TextRun({ text: pricingData.laborHoursSummary.fteEquivalent, size: 24, color: COLORS.primary }),
        ],
        spacing: { before: 100, after: 150 },
      }));
    }
  }

  // ==================== LABOR CATEGORIES WITH SCA/GSA ====================
  if (data.laborCategories && data.laborCategories.length > 0) {
    elements.push(createSectionHeading('Labor Categories & Rate Structure', 2));

    const laborRows = data.laborCategories.map((cat: any) => [
      cat.category,
      cat.scaEquivalent || cat.equivalent || '-',
      cat.gsaEquivalent || '-',
      cat.minExperience,
      cat.education,
      cat.hourlyRate,
      cat.loadedRate,
    ]);

    elements.push(createStyledTable(
      ['Category', 'SCA Equiv', 'GSA Equiv', 'Experience', 'Education', 'Hourly', 'Loaded'],
      laborRows,
      { headerBgColor: COLORS.greenBg }
    ));
  }

  // ==================== LABOR RATE BUILDUP ====================
  if (pricingData.laborRateBuildup?.buildupTable?.length > 0) {
    elements.push(createSectionHeading('Labor Rate Buildup (Base → Loaded Rate)', 2));

    if (pricingData.laborRateBuildup.narrative) {
      elements.push(createParagraph(pricingData.laborRateBuildup.narrative));
    }

    const buildupRows = pricingData.laborRateBuildup.buildupTable.map((row: any) => [
      row.laborCategory || '',
      row.baseRate || '',
      row.fringeRate || '',
      row.subtotalWithFringe || '',
      row.overheadRate || '',
      row.subtotalWithOverhead || '',
      row.gaRate || '',
      row.totalCost || '',
      row.profitRate || '',
      row.loadedRate || '',
    ]);

    elements.push(createStyledTable(
      ['Category', 'Base', '+Fringe', 'Subtotal', '+Overhead', 'Subtotal', '+G&A', 'Total Cost', '+Profit', 'Loaded'],
      buildupRows,
      { headerBgColor: COLORS.purpleBg }
    ));
  }

  // ==================== INDIRECT RATES ====================
  if (data.indirectRates && data.indirectRates.length > 0) {
    elements.push(createSectionHeading('Indirect Rate Structure', 2));

    const indirectRows = data.indirectRates.map((rate: any) => [
      rate.category,
      rate.rate,
      rate.basis,
      rate.auditStatus,
      (rate.components || []).join(', ') || '-',
    ]);

    elements.push(createStyledTable(
      ['Category', 'Rate', 'Basis', 'Audit Status', 'Components'],
      indirectRows
    ));
  }

  // ==================== SCA WAGE DETERMINATION ====================
  if (pricingData.scaWageDetermination?.applicable) {
    elements.push(createSectionHeading('Service Contract Act (SCA) Wage Determination', 2));

    const scaData = pricingData.scaWageDetermination;
    elements.push(new Paragraph({
      children: [
        new TextRun({ text: 'Wage Decision: ', bold: true, size: 24}),
        new TextRun({ text: scaData.wageDecisionNumber || 'N/A', size: 24}),
        new TextRun({ text: '  |  Locality: ', bold: true, size: 24}),
        new TextRun({ text: scaData.locality || 'N/A', size: 24}),
      ],
      shading: { fill: 'FEF3C7', type: ShadingType.CLEAR },
      spacing: { after: 150 },
    }));

    if (scaData.minimumWages?.length > 0) {
      const wageRows = scaData.minimumWages.map((wage: any) => [
        wage.category || '',
        wage.minimumRate || '',
        wage.proposedRate || '',
        wage.healthAndWelfare || '',
      ]);

      elements.push(createStyledTable(
        ['Category', 'SCA Minimum', 'Proposed Rate', 'H&W'],
        wageRows,
        { headerBgColor: 'FEF3C7' }
      ));
    }
  }

  // ==================== COST BREAKDOWN ====================
  if (data.costBreakdown?.headers && data.costBreakdown?.rows) {
    elements.push(createSectionHeading('Cost Summary by Period', 2));

    if (data.costBreakdown.assumptions) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: data.costBreakdown.assumptions, size: 24, italics: true, color: COLORS.grayLight })],
        spacing: { after: 100 },
      }));
    }

    const costRows = data.costBreakdown.rows.map((r: any) => [r.element, ...(r.values || [])]);
    elements.push(createStyledTable(data.costBreakdown.headers, costRows));

    if ((data.costBreakdown as any).costNarrative) {
      elements.push(new Paragraph({
        children: [
          new TextRun({ text: 'Cost Narrative: ', bold: true, size: 24 }),
          new TextRun({ text: (data.costBreakdown as any).costNarrative, size: 24 }),
        ],
        shading: { fill: COLORS.grayBg, type: ShadingType.CLEAR },
        spacing: { before: 100, after: 150 },
      }));
    }

    if ((data.costBreakdown as any).totalProjectedCost) {
      elements.push(new Paragraph({
        children: [
          new TextRun({ text: 'Total Projected Cost: ', bold: true, size: 24 }),
          new TextRun({ text: (data.costBreakdown as any).totalProjectedCost, bold: true, size: 28, color: COLORS.success }),
        ],
        shading: { fill: COLORS.greenBg, type: ShadingType.CLEAR },
        spacing: { before: 100, after: 200 },
        alignment: AlignmentType.CENTER,
      }));
    }
  }

  // ==================== BASIS OF ESTIMATE ====================
  if (pricingData.basisOfEstimate) {
    elements.push(createSectionHeading('Basis of Estimate (BOE) - FAR 15-2 Section I.C', 2));

    const boe = pricingData.basisOfEstimate;

    if (boe.estimateConfidenceLevel) {
      elements.push(new Paragraph({
        children: [
          new TextRun({ text: 'Estimate Confidence: ', bold: true, size: 24}),
          new TextRun({ text: boe.estimateConfidenceLevel, bold: true, size: 24, color: COLORS.success }),
        ],
        shading: { fill: COLORS.blueBg, type: ShadingType.CLEAR },
        spacing: { after: 150 },
      }));
    }

    if (boe.methodology) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Methodology', bold: true, size: 24})],
        spacing: { after: 60 },
      }));
      elements.push(createParagraph(boe.methodology));
    }

    if (boe.laborHoursJustification) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Labor Hours Justification', bold: true, size: 24})],
        spacing: { before: 100, after: 60 },
      }));
      elements.push(createParagraph(boe.laborHoursJustification));
    }

    if (boe.assumptions?.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Key Assumptions', bold: true, size: 24})],
        spacing: { before: 100, after: 60 },
      }));
      boe.assumptions.forEach((assumption: string) => {
        elements.push(createBulletPoint(assumption));
      });
    }

    if (boe.risks?.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Risk Factors & Mitigations', bold: true, size: 24})],
        spacing: { before: 100, after: 60 },
      }));
      boe.risks.forEach((risk: string) => {
        elements.push(createBulletPoint(risk));
      });
    }
  }

  // ==================== OTHER DIRECT COSTS ====================
  if (pricingData.otherDirectCosts?.length > 0) {
    elements.push(createSectionHeading('Other Direct Costs (ODCs)', 2));

    const odcRows = pricingData.otherDirectCosts.map((odc: any) => [
      odc.category || '',
      odc.description || '',
      String(odc.quantity || ''),
      odc.unitCost || '',
      odc.annualTotal || '',
      odc.totalAllYears || odc.total || '',
    ]);

    elements.push(createStyledTable(
      ['Category', 'Description', 'Qty', 'Unit Cost', 'Annual', 'Total'],
      odcRows
    ));
  }

  // ==================== VALUE PROPOSITION ====================
  if (data.valueProposition) {
    elements.push(createSectionHeading('Best Value Determination', 2));

    if (data.valueProposition.efficiencyMeasures && data.valueProposition.efficiencyMeasures.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Efficiency Measures', bold: true, size: 24})],
        shading: { fill: COLORS.greenBg, type: ShadingType.CLEAR },
        spacing: { after: 80 },
      }));
      data.valueProposition.efficiencyMeasures.forEach(measure => {
        elements.push(new Paragraph({
          children: [
            new TextRun({ text: '✓ ', size: 24, color: COLORS.success }),
            new TextRun({ text: measure, size: 24}),
          ],
          shading: { fill: COLORS.greenBg, type: ShadingType.CLEAR },
          spacing: { after: 60 },
        }));
      });
    }

    if ((data.valueProposition as any).costSavingsApproach) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Cost Savings Approach', bold: true, size: 24})],
        spacing: { before: 150, after: 80 },
      }));
      elements.push(createParagraph((data.valueProposition as any).costSavingsApproach));
    }

    if (data.valueProposition.bestValueDetermination) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Best Value Summary', bold: true, size: 24})],
        spacing: { before: 150, after: 80 },
      }));
      elements.push(createParagraph(data.valueProposition.bestValueDetermination));
    }
  }

  // ==================== COST CERTIFICATION ====================
  if (pricingData.costCertification) {
    elements.push(createSectionHeading('Cost Certification Statement (FAR 15.406-2)', 2));

    const cert = pricingData.costCertification;

    elements.push(new Paragraph({
      children: [new TextRun({
        text: cert.certificationText || 'CERTIFICATE OF CURRENT COST OR PRICING DATA: This is to certify that, to the best of my knowledge and belief, the cost or pricing data submitted are accurate, complete, and current as of the date of agreement on price.',
        size: 24,
        italics: true,
      })],
      shading: { fill: 'FEE2E2', type: ShadingType.CLEAR },
      spacing: { after: 150 },
      border: {
        left: { style: BorderStyle.SINGLE, size: 24, color: COLORS.danger },
      },
    }));

    const certRows: string[][] = [
      ['Applicability', cert.applicability || 'Required for contracts exceeding $2,000,000 threshold per FAR 15.403-4'],
      ['Exceptions Claimed', cert.exceptionsClaimed || 'None'],
      ['Data Submitted', cert.dataSubmitted || 'Labor rates, indirect rates, material costs, subcontractor quotes'],
      ['Data Currency Date', cert.currencyDate || '[TO BE COMPLETED AT SUBMISSION]'],
    ];

    elements.push(createStyledTable(['Item', 'Details'], certRows, { headerBgColor: 'FEE2E2' }));

    elements.push(new Paragraph({
      children: [new TextRun({
        text: 'Note: This certification will be executed by an authorized company representative at the time of proposal submission. The Truth in Negotiations Act (TINA) requires that cost or pricing data be accurate, complete, and current. Defective pricing may result in price reduction and potential penalties.',
        size: 24,
        italics: true,
        color: COLORS.grayLight,
      })],
      spacing: { before: 100, after: 100 },
    }));
  }

  // Rate Methodology (existing - moved to end)
  if (data.rateMethodology && !pricingData.laborRateBuildup) {
    elements.push(createSectionHeading('Rate Development Methodology', 2));
    elements.push(createParagraph(data.rateMethodology));
  }

  return elements;
}

// ============================================================================
// COMPLIANCE EXPORTER
// ============================================================================

function exportCompliance(data: ComplianceContent): Paragraph[] {
  const elements: Paragraph[] = [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant': return COLORS.success;
      case 'partial': return COLORS.warning;
      case 'gap': return COLORS.danger;
      default: return COLORS.gray;
    }
  };

  // FAR Compliance
  if (data.farCompliance && data.farCompliance.length > 0) {
    elements.push(createSectionHeading('FAR Compliance Matrix', 2));

    // Create table with status indicators
    const table = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: ['Clause', 'Title', 'Status', 'Notes'].map(header =>
            new TableCell({
              children: [new Paragraph({
                children: [new TextRun({ text: header, bold: true, size: 24 })],
              })],
              shading: { fill: COLORS.grayBg, type: ShadingType.CLEAR },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                bottom: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                left: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                right: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
              },
            })
          ),
        }),
        ...data.farCompliance.map(item =>
          new TableRow({
            children: [
              new TableCell({
                children: [new Paragraph({
                  children: [new TextRun({ text: item.clause, size: 24, font: 'Times New Roman' })],
                })],
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                  bottom: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                  left: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                  right: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                },
              }),
              new TableCell({
                children: [new Paragraph({
                  children: [new TextRun({ text: item.title, size: 24 })],
                })],
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                  bottom: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                  left: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                  right: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                },
              }),
              new TableCell({
                children: [new Paragraph({
                  children: [new TextRun({
                    text: item.status.toUpperCase(),
                    bold: true,
                    size: 24,
                    color: getStatusColor(item.status),
                  })],
                  alignment: AlignmentType.CENTER,
                })],
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                  bottom: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                  left: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                  right: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                },
              }),
              new TableCell({
                children: [new Paragraph({
                  children: [new TextRun({ text: item.notes, size: 24 })],
                })],
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                  bottom: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                  left: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                  right: { style: BorderStyle.SINGLE, size: 1, color: COLORS.grayBorder },
                },
              }),
            ],
          })
        ),
      ],
    });
    elements.push(table);
  }

  // DFARS Compliance
  if (data.dfarsCompliance && data.dfarsCompliance.length > 0) {
    elements.push(createSectionHeading('DFARS Compliance Matrix', 2));
    elements.push(createStyledTable(
      ['Clause', 'Title', 'Status', 'Notes'],
      data.dfarsCompliance.map(item => [item.clause, item.title, item.status.toUpperCase(), item.notes])
    ));
  }

  // Certifications
  if (data.certifications && data.certifications.length > 0) {
    elements.push(createSectionHeading('Certifications & Registrations', 2));

    data.certifications.forEach(cert => {
      elements.push(new Paragraph({
        children: [
          new TextRun({ text: cert.name, bold: true, size: 24}),
          new TextRun({ text: ` — ${cert.status}`, size: 24, color: COLORS.success }),
        ],
        spacing: { before: 100, after: 40 },
      }));
      if (cert.number) {
        elements.push(new Paragraph({
          children: [new TextRun({ text: cert.number, size: 24, color: COLORS.grayLight })],
          spacing: { after: 40 },
          indent: { left: convertInchesToTwip(0.25) },
        }));
      }
      if (cert.expiration) {
        elements.push(new Paragraph({
          children: [new TextRun({ text: `Expires: ${cert.expiration}`, size: 24, color: COLORS.grayLight })],
          spacing: { after: 80 },
          indent: { left: convertInchesToTwip(0.25) },
        }));
      }
    });
  }

  // Security Clearances
  if (data.securityClearances && data.securityClearances.length > 0) {
    elements.push(createSectionHeading('Security Clearances', 2));

    data.securityClearances.forEach(clearance => {
      elements.push(new Paragraph({
        children: [
          new TextRun({ text: `${clearance.type}: `, bold: true, size: 24}),
          new TextRun({ text: clearance.level, bold: true, size: 24, color: COLORS.purple }),
        ],
        shading: { fill: COLORS.purpleBg, type: ShadingType.CLEAR },
        spacing: { before: 100, after: 40 },
      }));
      elements.push(new Paragraph({
        children: [new TextRun({ text: clearance.status, size: 24, color: COLORS.grayLight })],
        shading: { fill: COLORS.purpleBg, type: ShadingType.CLEAR },
        spacing: { after: 40 },
      }));
      if (clearance.agency) {
        elements.push(new Paragraph({
          children: [new TextRun({ text: `Agency: ${clearance.agency}`, size: 24, color: COLORS.grayLight })],
          shading: { fill: COLORS.purpleBg, type: ShadingType.CLEAR },
          spacing: { after: 100 },
        }));
      }
    });
  }

  // Acknowledgments
  elements.push(createSectionHeading('Compliance Acknowledgments', 2));
  elements.push(new Paragraph({
    children: [new TextRun({ text: stripCitations(data.acknowledgments), size: 24})],
    shading: { fill: COLORS.grayBg, type: ShadingType.CLEAR },
    spacing: { after: 200 },
  }));

  return elements;
}

// ============================================================================
// DELIVERABLES EXPORTER
// ============================================================================

function exportDeliverables(data: DeliverablesData): (Paragraph | Table)[] {
  const elements: (Paragraph | Table)[] = [];

  const {
    deliverables_with_labor,
    labor_summary,
    pricing_inputs,
    compliance_framework,
    timeline_summary,
    volume_summary,
  } = data;

  // ==================== OVERVIEW SUMMARY ====================
  elements.push(createSectionHeading('Deliverables Overview', 2));

  // Summary stats table
  const summaryRows: string[][] = [
    ['Total Deliverables', String(deliverables_with_labor?.length || 0)],
    ['Estimated Hours (Recommended)', String(labor_summary?.total_hours?.recommended?.toLocaleString() || 0)],
    ['Hours Range', `${labor_summary?.total_hours?.min?.toLocaleString() || 0} - ${labor_summary?.total_hours?.max?.toLocaleString() || 0}`],
    ['Labor Categories', String(labor_summary?.by_category?.length || 0)],
    ['Rate Basis', (pricing_inputs?.suggested_rate_basis || 'Market').toUpperCase()],
  ];

  if (labor_summary?.total_hours?.ceiling) {
    summaryRows.push(['Hours Ceiling', labor_summary.total_hours.ceiling.toLocaleString()]);
  }

  elements.push(createStyledTable(['Metric', 'Value'], summaryRows, { headerBgColor: COLORS.blueBg }));

  // Timeline summary if available
  if (timeline_summary) {
    elements.push(new Paragraph({
      children: [
        new TextRun({ text: 'Period of Performance: ', bold: true, size: 24}),
        new TextRun({ text: timeline_summary.period_of_performance, size: 24}),
      ],
      spacing: { before: 200, after: 100 },
    }));

    if (timeline_summary.pace_requirements) {
      elements.push(new Paragraph({
        children: [
          new TextRun({ text: 'Pace Requirements: ', bold: true, size: 24}),
          new TextRun({ text: timeline_summary.pace_requirements, size: 24}),
        ],
        spacing: { after: 100 },
      }));
    }

    if (timeline_summary.key_milestones && timeline_summary.key_milestones.length > 0) {
      elements.push(createSectionHeading('Key Milestones', 3));
      elements.push(createStyledTable(
        ['Date', 'Milestone'],
        timeline_summary.key_milestones.map(m => [m.date, m.milestone])
      ));
    }
  }

  // ==================== DELIVERABLES LIST ====================
  elements.push(createSectionHeading('Deliverables Breakdown', 2));

  deliverables_with_labor?.forEach((deliverable, idx) => {
    // Deliverable header
    elements.push(new Paragraph({
      children: [
        new TextRun({ text: `${idx + 1}. `, bold: true, size: 24, color: COLORS.primary }),
        new TextRun({ text: deliverable.deliverable_name, bold: true, size: 24, color: COLORS.gray }),
      ],
      spacing: { before: 300, after: 100 },
      border: {
        bottom: { style: BorderStyle.SINGLE, size: 8, color: COLORS.grayBorder },
      },
    }));

    // ID and type
    const metaInfo: string[] = [];
    if (deliverable.deliverable_id) metaInfo.push(`ID: ${deliverable.deliverable_id}`);
    if (deliverable.type) metaInfo.push(`Type: ${deliverable.type}`);
    if (deliverable.quantity) metaInfo.push(`Quantity: ${deliverable.quantity}${deliverable.unit ? ' ' + deliverable.unit : ''}`);

    if (metaInfo.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: metaInfo.join(' | '), size: 24, color: COLORS.grayLight })],
        spacing: { after: 100 },
      }));
    }

    // Description
    if (deliverable.description) {
      elements.push(createParagraph(deliverable.description));
    }

    // Hours estimate
    elements.push(new Paragraph({
      children: [
        new TextRun({ text: 'Estimated Hours: ', bold: true, size: 24 }),
        new TextRun({ text: `${deliverable.total_hours?.recommended?.toLocaleString() || 0} hours`, size: 24, color: COLORS.primary }),
        new TextRun({ text: ` (Range: ${deliverable.total_hours?.min?.toLocaleString() || 0} - ${deliverable.total_hours?.max?.toLocaleString() || 0})`, size: 24, color: COLORS.grayLight }),
      ],
      spacing: { before: 100, after: 100 },
    }));

    // Labor phases
    if (deliverable.labor_phases && deliverable.labor_phases.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Labor Phases:', bold: true, size: 24 })],
        spacing: { before: 100, after: 60 },
      }));

      deliverable.labor_phases.forEach(phase => {
        elements.push(new Paragraph({
          children: [
            new TextRun({ text: `• ${phase.phase_name}`, bold: true, size: 24 }),
            new TextRun({ text: ` — ${phase.hours_estimate?.recommended || 0} hours`, size: 24, color: COLORS.grayLight }),
          ],
          spacing: { after: 40 },
          indent: { left: convertInchesToTwip(0.25) },
        }));

        if (phase.description) {
          elements.push(new Paragraph({
            children: [new TextRun({ text: phase.description, size: 24, color: COLORS.grayLight })],
            spacing: { after: 40 },
            indent: { left: convertInchesToTwip(0.5) },
          }));
        }

        // Labor categories for this phase
        if (phase.labor_categories && phase.labor_categories.length > 0) {
          const catText = phase.labor_categories.map(c => `${c.category} (${c.hours_percent}%)`).join(', ');
          elements.push(new Paragraph({
            children: [new TextRun({ text: `Labor: ${catText}`, size: 24, color: COLORS.grayLight })],
            spacing: { after: 60 },
            indent: { left: convertInchesToTwip(0.5) },
          }));
        }
      });
    }

    // Specifications
    if (deliverable.specifications) {
      const specs = deliverable.specifications;
      if (specs.format) {
        elements.push(createLabeledField('Format', specs.format));
      }
      if (specs.quality_standards && specs.quality_standards.length > 0) {
        elements.push(new Paragraph({
          children: [
            new TextRun({ text: 'Quality Standards: ', bold: true, size: 24 }),
            new TextRun({ text: specs.quality_standards.join(', '), size: 24 }),
          ],
          spacing: { after: 60 },
          indent: { left: convertInchesToTwip(0.25) },
        }));
      }
      if (specs.acceptance_criteria && specs.acceptance_criteria.length > 0) {
        elements.push(new Paragraph({
          children: [new TextRun({ text: 'Acceptance Criteria:', bold: true, size: 24 })],
          spacing: { after: 40 },
          indent: { left: convertInchesToTwip(0.25) },
        }));
        specs.acceptance_criteria.forEach(crit => {
          elements.push(createBulletPoint(crit, 2));
        });
      }
    }

    // Timeline
    if (deliverable.timeline) {
      const timeline = deliverable.timeline;
      if (timeline.due_date) {
        elements.push(createLabeledField('Due Date', timeline.due_date));
      }
      if (timeline.duration) {
        elements.push(createLabeledField('Duration', timeline.duration));
      }
    }
  });

  // ==================== LABOR SUMMARY TABLE ====================
  if (labor_summary?.by_category && labor_summary.by_category.length > 0) {
    elements.push(createSectionHeading('Labor Summary by Category', 2));

    const laborRows = labor_summary.by_category.map(cat => [
      cat.category,
      `${cat.total_hours?.min?.toLocaleString() || 0} - ${cat.total_hours?.max?.toLocaleString() || 0}`,
      cat.sca_applicable ? 'Yes' : 'No',
      cat.sca_title || '-',
    ]);

    elements.push(createStyledTable(
      ['Labor Category', 'Hours Range', 'SCA Applicable', 'SCA Title'],
      laborRows,
      { headerBgColor: COLORS.greenBg }
    ));
  }

  // ==================== PRICING INPUTS ====================
  if (pricing_inputs) {
    elements.push(createSectionHeading('Pricing Inputs', 2));

    if (pricing_inputs.labor_categories_required && pricing_inputs.labor_categories_required.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Required Labor Categories:', bold: true, size: 24})],
        spacing: { after: 80 },
      }));

      pricing_inputs.labor_categories_required.forEach(cat => {
        elements.push(createBulletPoint(cat));
      });
    }

    elements.push(new Paragraph({
      children: [
        new TextRun({ text: 'Suggested Rate Basis: ', bold: true, size: 24}),
        new TextRun({ text: (pricing_inputs.suggested_rate_basis || 'Market').toUpperCase(), size: 24, color: COLORS.success }),
      ],
      spacing: { before: 150, after: 80 },
    }));

    elements.push(new Paragraph({
      children: [
        new TextRun({ text: 'SCA Wage Determination: ', bold: true, size: 24}),
        new TextRun({
          text: pricing_inputs.sca_wage_determination_needed ? 'Required' : 'Not Required',
          size: 24,
          color: pricing_inputs.sca_wage_determination_needed ? COLORS.warning : COLORS.grayLight,
        }),
      ],
      spacing: { after: 100 },
    }));
  }

  // ==================== COMPLIANCE FRAMEWORK ====================
  if (compliance_framework) {
    elements.push(createSectionHeading('Compliance Framework', 2));

    const complianceItems: string[][] = [];

    if (compliance_framework.section_508) {
      complianceItems.push(['Section 508 Accessibility', 'Required']);
    }
    if (compliance_framework.sca_applicable) {
      complianceItems.push(['Service Contract Act (SCA)', 'Applicable']);
    }
    if (compliance_framework.privacy_act) {
      complianceItems.push(['Privacy Act', 'Applicable']);
    }
    if (compliance_framework.fisma) {
      complianceItems.push(['FISMA', 'Applicable']);
    }

    if (complianceItems.length > 0) {
      elements.push(createStyledTable(['Requirement', 'Status'], complianceItems));
    }

    if (compliance_framework.far_clauses && compliance_framework.far_clauses.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'FAR Clauses:', bold: true, size: 24})],
        spacing: { before: 150, after: 80 },
      }));
      compliance_framework.far_clauses.forEach(clause => {
        elements.push(createBulletPoint(clause));
      });
    }

    if (compliance_framework.dfar_clauses && compliance_framework.dfar_clauses.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'DFARS Clauses:', bold: true, size: 24})],
        spacing: { before: 150, after: 80 },
      }));
      compliance_framework.dfar_clauses.forEach(clause => {
        elements.push(createBulletPoint(clause));
      });
    }

    if (compliance_framework.agency_specific && compliance_framework.agency_specific.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Agency-Specific Requirements:', bold: true, size: 24})],
        spacing: { before: 150, after: 80 },
      }));
      compliance_framework.agency_specific.forEach(req => {
        elements.push(createBulletPoint(req));
      });
    }
  }

  return elements;
}

// ============================================================================
// PRODUCT DEFINITION EXPORTER
// ============================================================================

function exportProductDefinition(data: ProductDefinition): (Paragraph | Table)[] {
  const elements: (Paragraph | Table)[] = [];

  // ==================== HEADER / EXECUTIVE SUMMARY ====================
  elements.push(new Paragraph({
    children: [
      new TextRun({ text: data.product_name, bold: true, size: 32, color: COLORS.primary }),
    ],
    spacing: { after: 100 },
  }));

  elements.push(new Paragraph({
    children: [
      new TextRun({ text: `${data.delivery_model.type.toUpperCase()} | ${data.product_type}`, size: 24, color: COLORS.grayLight }),
    ],
    spacing: { after: 200 },
  }));

  elements.push(new Paragraph({
    children: [new TextRun({ text: data.executive_summary, size: 24})],
    shading: { fill: COLORS.blueBg, type: ShadingType.CLEAR },
    spacing: { after: 300 },
  }));

  // ==================== BID RECOMMENDATION ====================
  const bidRec = data.bid_recommendation;
  const bidColor = bidRec.recommendation === 'strong-bid' || bidRec.recommendation === 'bid'
    ? COLORS.success
    : bidRec.recommendation === 'consider'
      ? COLORS.warning
      : COLORS.danger;

  elements.push(createSectionHeading('Bid Recommendation', 2));
  elements.push(new Paragraph({
    children: [
      new TextRun({
        text: bidRec.recommendation.replace('-', ' ').toUpperCase(),
        bold: true,
        size: 28,
        color: bidColor,
      }),
    ],
    spacing: { after: 100 },
  }));

  bidRec.rationale.forEach(reason => {
    elements.push(createBulletPoint(reason));
  });

  if (bidRec.key_requirements.length > 0) {
    elements.push(new Paragraph({
      children: [new TextRun({ text: 'Key Requirements to Win:', bold: true, size: 24 })],
      spacing: { before: 150, after: 80 },
    }));
    bidRec.key_requirements.forEach(req => {
      elements.push(createBulletPoint(req, 1));
    });
  }

  if (bidRec.capability_gaps.length > 0) {
    elements.push(new Paragraph({
      children: [new TextRun({ text: 'Capability Gaps:', bold: true, size: 24, color: COLORS.danger })],
      spacing: { before: 150, after: 80 },
    }));
    bidRec.capability_gaps.forEach(gap => {
      elements.push(createBulletPoint(gap, 1));
    });
  }

  // ==================== DELIVERY MODEL ====================
  elements.push(createSectionHeading('Delivery Model', 2));
  elements.push(new Paragraph({
    children: [
      new TextRun({ text: `Model: ${data.delivery_model.type.toUpperCase()}`, bold: true, size: 24}),
    ],
    spacing: { after: 100 },
  }));
  elements.push(createParagraph(data.delivery_model.explanation));

  elements.push(new Paragraph({
    children: [new TextRun({ text: 'Rationale:', bold: true, size: 24 })],
    spacing: { before: 100, after: 60 },
  }));
  data.delivery_model.rationale.forEach(reason => {
    elements.push(createBulletPoint(reason));
  });

  // ==================== SCOPE OF WORK ====================
  if (data.scope_of_work) {
    elements.push(createSectionHeading('Scope of Work', 2));
    elements.push(createParagraph(data.scope_of_work.overview));

    if (data.scope_of_work.included.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Included in Scope:', bold: true, size: 24, color: COLORS.success })],
        spacing: { before: 150, after: 80 },
      }));
      data.scope_of_work.included.forEach(item => {
        elements.push(new Paragraph({
          children: [
            new TextRun({ text: '✓ ', size: 24, color: COLORS.success }),
            new TextRun({ text: item, size: 24}),
          ],
          indent: { left: convertInchesToTwip(0.25) },
          spacing: { after: 60 },
        }));
      });
    }

    if (data.scope_of_work.excluded && data.scope_of_work.excluded.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Excluded from Scope:', bold: true, size: 24, color: COLORS.danger })],
        spacing: { before: 150, after: 80 },
      }));
      data.scope_of_work.excluded.forEach(item => {
        elements.push(new Paragraph({
          children: [
            new TextRun({ text: '✗ ', size: 24, color: COLORS.danger }),
            new TextRun({ text: item, size: 24}),
          ],
          indent: { left: convertInchesToTwip(0.25) },
          spacing: { after: 60 },
        }));
      });
    }

    if (data.scope_of_work.assumptions && data.scope_of_work.assumptions.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Assumptions:', bold: true, size: 24})],
        spacing: { before: 150, after: 80 },
      }));
      data.scope_of_work.assumptions.forEach(item => {
        elements.push(createBulletPoint(item));
      });
    }
  }

  // ==================== PRODUCT COMPONENTS ====================
  if (data.components && data.components.length > 0) {
    elements.push(createSectionHeading('Product Components', 2));

    data.components.forEach((comp, idx) => {
      elements.push(new Paragraph({
        children: [
          new TextRun({ text: `${comp.id} — `, size: 24, color: COLORS.grayLight }),
          new TextRun({ text: comp.name, bold: true, size: 24}),
          new TextRun({ text: ` [${comp.category}]`, size: 24, color: COLORS.primary }),
        ],
        spacing: { before: 200, after: 80 },
      }));

      elements.push(createParagraph(comp.description));

      elements.push(new Paragraph({
        children: [
          new TextRun({ text: 'User Value: ', bold: true, size: 24 }),
          new TextRun({ text: comp.user_value, size: 24, color: COLORS.success }),
        ],
        spacing: { after: 60 },
      }));

      if (comp.frequency) {
        elements.push(createLabeledField('Frequency', comp.frequency));
      }

      if (comp.staffing) {
        elements.push(createLabeledField('Staffing', comp.staffing));
      }

      if (comp.key_activities && comp.key_activities.length > 0) {
        elements.push(new Paragraph({
          children: [new TextRun({ text: 'Key Activities:', bold: true, size: 24 })],
          spacing: { after: 40 },
        }));
        comp.key_activities.forEach(act => {
          elements.push(createBulletPoint(act, 1));
        });
      }

      if (comp.quality_standards && comp.quality_standards.length > 0) {
        elements.push(new Paragraph({
          children: [
            new TextRun({ text: 'Quality Standards: ', bold: true, size: 24 }),
            new TextRun({ text: comp.quality_standards.join(', '), size: 24 }),
          ],
          spacing: { after: 60 },
        }));
      }

      if (comp.deliverables_mapped.length > 0) {
        elements.push(new Paragraph({
          children: [
            new TextRun({ text: 'Maps to Deliverables: ', bold: true, size: 24, color: COLORS.grayLight }),
            new TextRun({ text: comp.deliverables_mapped.join(', '), size: 24, color: COLORS.grayLight }),
          ],
          spacing: { after: 100 },
        }));
      }
    });
  }

  // ==================== TECHNICAL APPROACH ====================
  if (data.technical_approach) {
    elements.push(createSectionHeading('Technical Approach', 2));
    elements.push(createParagraph(data.technical_approach.methodology));

    if (data.technical_approach.technology_stack && data.technical_approach.technology_stack.length > 0) {
      elements.push(new Paragraph({
        children: [
          new TextRun({ text: 'Technology Stack: ', bold: true, size: 24}),
          new TextRun({ text: data.technical_approach.technology_stack.join(', '), size: 24}),
        ],
        spacing: { before: 100, after: 100 },
      }));
    }

    if (data.technical_approach.tools_and_equipment && data.technical_approach.tools_and_equipment.length > 0) {
      elements.push(new Paragraph({
        children: [
          new TextRun({ text: 'Tools & Equipment: ', bold: true, size: 24}),
          new TextRun({ text: data.technical_approach.tools_and_equipment.join(', '), size: 24}),
        ],
        spacing: { after: 100 },
      }));
    }

    if (data.technical_approach.innovation_highlights && data.technical_approach.innovation_highlights.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Innovation Highlights:', bold: true, size: 24})],
        shading: { fill: COLORS.purpleBg, type: ShadingType.CLEAR },
        spacing: { before: 100, after: 80 },
      }));
      data.technical_approach.innovation_highlights.forEach(item => {
        elements.push(new Paragraph({
          children: [
            new TextRun({ text: '★ ', size: 24, color: COLORS.purple }),
            new TextRun({ text: item, size: 24}),
          ],
          shading: { fill: COLORS.purpleBg, type: ShadingType.CLEAR },
          spacing: { after: 60 },
        }));
      });
    }
  }

  // ==================== STAFFING MODEL ====================
  if (data.staffing_model) {
    elements.push(createSectionHeading('Staffing Model', 2));

    if (data.staffing_model.total_fte) {
      elements.push(new Paragraph({
        children: [
          new TextRun({ text: 'Total FTE: ', bold: true, size: 24}),
          new TextRun({ text: String(data.staffing_model.total_fte), size: 24, color: COLORS.success }),
        ],
        spacing: { after: 100 },
      }));
    }

    if (data.staffing_model.supervision_ratio) {
      elements.push(createLabeledField('Supervision Ratio', data.staffing_model.supervision_ratio));
    }

    if (data.staffing_model.roles && data.staffing_model.roles.length > 0) {
      const roleRows = data.staffing_model.roles.map(role => [
        role.role,
        String(role.quantity),
        role.responsibilities.slice(0, 2).join('; '),
        (role.qualifications || []).slice(0, 2).join(', ') || '-',
      ]);

      elements.push(createStyledTable(
        ['Role', 'Quantity', 'Key Responsibilities', 'Qualifications'],
        roleRows,
        { headerBgColor: COLORS.greenBg }
      ));
    }

    if (data.staffing_model.key_personnel && data.staffing_model.key_personnel.length > 0) {
      elements.push(new Paragraph({
        children: [
          new TextRun({ text: 'Key Personnel: ', bold: true, size: 24}),
          new TextRun({ text: data.staffing_model.key_personnel.join(', '), size: 24}),
        ],
        spacing: { before: 100, after: 100 },
      }));
    }
  }

  // ==================== SERVICE LEVELS ====================
  if (data.service_levels && data.service_levels.length > 0) {
    elements.push(createSectionHeading('Service Levels / SLAs', 2));

    const slaRows = data.service_levels.map(sla => [
      sla.metric_name,
      sla.target,
      sla.measurement_method,
      sla.reporting_frequency,
    ]);

    elements.push(createStyledTable(
      ['Metric', 'Target', 'Measurement', 'Reporting'],
      slaRows,
      { headerBgColor: COLORS.blueBg }
    ));
  }

  // ==================== IMPLEMENTATION PLAN ====================
  if (data.implementation_plan) {
    elements.push(createSectionHeading('Implementation Plan', 2));
    elements.push(createParagraph(data.implementation_plan.approach));

    if (data.implementation_plan.transition_period) {
      elements.push(createLabeledField('Transition Period', data.implementation_plan.transition_period));
    }

    if (data.implementation_plan.phases && data.implementation_plan.phases.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Phases:', bold: true, size: 24})],
        spacing: { before: 150, after: 80 },
      }));

      data.implementation_plan.phases.forEach((phase, idx) => {
        elements.push(new Paragraph({
          children: [
            new TextRun({ text: `${phase.phase_name}`, bold: true, size: 24}),
            new TextRun({ text: ` — ${phase.duration}`, size: 24, color: COLORS.grayLight }),
          ],
          spacing: { before: 100, after: 60 },
          border: { left: { style: BorderStyle.SINGLE, size: 24, color: COLORS.purple } },
          indent: { left: convertInchesToTwip(0.1) },
        }));

        elements.push(new Paragraph({
          children: [new TextRun({ text: phase.description, size: 24 })],
          spacing: { after: 60 },
          indent: { left: convertInchesToTwip(0.25) },
        }));

        if (phase.key_activities.length > 0) {
          elements.push(new Paragraph({
            children: [
              new TextRun({ text: 'Activities: ', bold: true, size: 24 }),
              new TextRun({ text: phase.key_activities.join(', '), size: 24 }),
            ],
            spacing: { after: 40 },
            indent: { left: convertInchesToTwip(0.25) },
          }));
        }

        if (phase.deliverables.length > 0) {
          elements.push(new Paragraph({
            children: [
              new TextRun({ text: 'Deliverables: ', bold: true, size: 24 }),
              new TextRun({ text: phase.deliverables.join(', '), size: 24 }),
            ],
            spacing: { after: 80 },
            indent: { left: convertInchesToTwip(0.25) },
          }));
        }
      });
    }

    if (data.implementation_plan.go_live_criteria && data.implementation_plan.go_live_criteria.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Go-Live Criteria:', bold: true, size: 24, color: COLORS.success })],
        shading: { fill: COLORS.greenBg, type: ShadingType.CLEAR },
        spacing: { before: 150, after: 80 },
      }));
      data.implementation_plan.go_live_criteria.forEach(crit => {
        elements.push(new Paragraph({
          children: [
            new TextRun({ text: '✓ ', size: 24, color: COLORS.success }),
            new TextRun({ text: crit, size: 24}),
          ],
          shading: { fill: COLORS.greenBg, type: ShadingType.CLEAR },
          spacing: { after: 60 },
        }));
      });
    }
  }

  // ==================== QUALITY ASSURANCE ====================
  if (data.quality_assurance) {
    elements.push(createSectionHeading('Quality Assurance', 2));
    elements.push(createParagraph(data.quality_assurance.approach));

    if (data.quality_assurance.inspection_frequency) {
      elements.push(createLabeledField('Inspection Frequency', data.quality_assurance.inspection_frequency));
    }

    if (data.quality_assurance.quality_metrics && data.quality_assurance.quality_metrics.length > 0) {
      elements.push(new Paragraph({
        children: [new TextRun({ text: 'Quality Metrics:', bold: true, size: 24})],
        spacing: { before: 100, after: 60 },
      }));
      data.quality_assurance.quality_metrics.forEach(metric => {
        elements.push(createBulletPoint(metric));
      });
    }

    if (data.quality_assurance.corrective_action_process) {
      elements.push(createLabeledField('Corrective Action Process', data.quality_assurance.corrective_action_process));
    }
  }

  // ==================== SUCCESS METRICS ====================
  if (data.success_metrics && data.success_metrics.length > 0) {
    elements.push(createSectionHeading('Success Metrics / KPIs', 2));

    const metricRows = data.success_metrics.map(m => [m.metric, m.target, m.measurement]);
    elements.push(createStyledTable(['Metric', 'Target', 'Measurement'], metricRows));
  }

  // ==================== USER IMPACT ====================
  if (data.user_impact) {
    elements.push(createSectionHeading('User Impact', 2));

    elements.push(new Paragraph({
      children: [
        new TextRun({ text: 'Primary Users: ', bold: true, size: 24}),
        new TextRun({ text: data.user_impact.primary_users.join(', '), size: 24}),
      ],
      spacing: { after: 80 },
    }));

    if (data.user_impact.user_count) {
      elements.push(createLabeledField('User Count', String(data.user_impact.user_count)));
    }

    elements.push(new Paragraph({
      children: [
        new TextRun({ text: 'User Benefit: ', bold: true, size: 24}),
        new TextRun({ text: data.user_impact.user_benefit, size: 24}),
      ],
      spacing: { after: 80 },
    }));

    elements.push(new Paragraph({
      children: [
        new TextRun({ text: 'Usage Scenario: ', bold: true, size: 24}),
        new TextRun({ text: data.user_impact.usage_scenario, size: 24}),
      ],
      spacing: { after: 100 },
    }));
  }

  // ==================== CONTRACT STRUCTURE ====================
  if (data.contract_structure) {
    elements.push(createSectionHeading('Contract Structure', 2));

    const contractRows: string[][] = [
      ['Contract Type', data.contract_structure.contract_type],
      ['Period of Performance', data.contract_structure.period_of_performance],
      ['Pricing Model', data.contract_structure.pricing_model],
    ];

    if (data.contract_structure.option_periods) {
      contractRows.push(['Option Periods', data.contract_structure.option_periods]);
    }

    elements.push(createStyledTable(['Element', 'Value'], contractRows));

    elements.push(new Paragraph({
      children: [
        new TextRun({ text: 'Pricing Rationale: ', bold: true, size: 24}),
        new TextRun({ text: data.contract_structure.pricing_rationale, size: 24}),
      ],
      spacing: { before: 100, after: 100 },
    }));
  }

  // ==================== DIFFERENTIATORS ====================
  if (data.differentiators && data.differentiators.length > 0) {
    elements.push(createSectionHeading('Competitive Differentiators', 2));
    data.differentiators.forEach(diff => {
      elements.push(new Paragraph({
        children: [
          new TextRun({ text: '✓ ', size: 24, color: COLORS.success }),
          new TextRun({ text: diff, size: 24}),
        ],
        shading: { fill: COLORS.greenBg, type: ShadingType.CLEAR },
        spacing: { after: 60 },
      }));
    });
  }

  // ==================== RISKS ====================
  if (data.risks && data.risks.length > 0) {
    elements.push(createSectionHeading('Risk Assessment', 2));

    data.risks.forEach(risk => {
      const impactColor = risk.impact === 'high' ? COLORS.danger
        : risk.impact === 'medium' ? COLORS.warning
          : COLORS.success;

      elements.push(new Paragraph({
        children: [
          new TextRun({ text: risk.risk, bold: true, size: 24}),
          new TextRun({ text: ` [${risk.impact.toUpperCase()}]`, size: 24, color: impactColor }),
        ],
        spacing: { before: 100, after: 60 },
      }));

      elements.push(new Paragraph({
        children: [
          new TextRun({ text: 'Mitigation: ', bold: true, size: 24 }),
          new TextRun({ text: risk.mitigation, size: 24 }),
        ],
        spacing: { after: 80 },
        indent: { left: convertInchesToTwip(0.25) },
      }));
    });
  }

  return elements;
}

// ============================================================================
// FALLBACK TEXT EXPORTER
// ============================================================================

// Strip inline markdown so the export never shows raw **, __, `, or # markers.
function stripInlineMarkdown(s: string): string {
  return s
    .replace(/\*\*(.+?)\*\*/g, '$1')   // **bold**
    .replace(/__(.+?)__/g, '$1')       // __bold__
    .replace(/(^|\s)\*(?!\s)(.+?)\*/g, '$1$2') // *italic*
    .replace(/`([^`]+)`/g, '$1')       // `code`
    .replace(/^\s*>\s?/, '')           // blockquote
    .replace(/^[-*_]{3,}\s*$/, '')     // --- / *** rules
    .replace(/\*+/g, '')               // stray asterisks
    .trim();
}

// Is this a markdown table row? (contains a pipe, not a separator handled below)
function isPipeRow(line: string): boolean {
  return /\|/.test(line) && line.replace(/[^|]/g, '').length >= 1;
}
function isPipeSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-{2,}.*$/.test(line) && /-/.test(line) && !/[a-z0-9]/i.test(line.replace(/[-:\s|]/g, ''));
}
function splitPipeCells(line: string): string[] {
  return line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(c => stripInlineMarkdown(c.trim()));
}

function exportFallbackText(content: string, _title: string): (Paragraph | Table)[] {
  const elements: (Paragraph | Table)[] = [];

  // Preserve line structure (toPlainText decodes entities but keeps newlines).
  const lines = toPlainText(content).split('\n').map(l => l.trim());

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw) continue;

    // ----- Markdown pipe table: gather the contiguous block -----
    if (isPipeRow(raw) && i + 1 < lines.length && (isPipeRow(lines[i + 1]) || isPipeSeparator(lines[i + 1]))) {
      const block: string[] = [];
      let j = i;
      while (j < lines.length && lines[j] && isPipeRow(lines[j])) { block.push(lines[j]); j++; }
      const rows = block.filter(l => !isPipeSeparator(l)).map(splitPipeCells);
      if (rows.length >= 1) {
        const headers = rows[0];
        const body = rows.slice(1);
        elements.push(createStyledTable(headers, body.length ? body : [headers.map(() => '')]));
        i = j - 1;
        continue;
      }
    }

    // ----- Markdown ATX heading (#, ##, ###) -----
    const md = raw.match(/^(#{1,6})\s+(.*)$/);
    if (md) {
      const level = Math.min(3, md[1].length) as 1 | 2 | 3;
      elements.push(createSectionHeading(stripInlineMarkdown(md[2]), level === 1 ? 2 : level));
      continue;
    }

    const cleaned = stripBracketAnnotations(stripInlineMarkdown(raw));
    if (!cleaned) continue;

    // ALL-CAPS short line → sub-heading
    if (cleaned === cleaned.toUpperCase() && cleaned.length < 60 && cleaned.length > 2) {
      elements.push(createSectionHeading(cleaned, 2));
    }
    // Bullet / numbered list item → real list formatting
    else if (/^[•\-*]\s+/.test(raw) || /^\d+[.)]\s+/.test(raw)) {
      elements.push(createBulletPoint(stripBracketAnnotations(stripInlineMarkdown(raw.replace(/^[•\-*]\s+/, '').replace(/^\d+[.)]\s+/, '')))));
    }
    // Regular paragraph
    else {
      elements.push(createParagraph(cleaned));
    }
  }

  return elements;
}

// ============================================================================
// HEADER/FOOTER GENERATORS
// ============================================================================

// Running header: company name (navy bold 9pt) left, "Solicitation N" (muted 9pt)
// right, thin navy rule beneath. Matches SerVet_Export_Reference.
function createDocumentHeader(companyName: string, solicitation: string): Header {
  return new Header({
    children: [
      new Paragraph({
        tabStops: [{ type: TabStopType.RIGHT, position: CONTENT_WIDTH_DXA }],
        border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: COLORS.navy } },
        spacing: { after: 40 },
        children: [
          new TextRun({ text: companyName || '', bold: true, size: FURN_SIZE, color: COLORS.navy }),
          ...(solicitation
            ? [new TextRun({ text: `\tSolicitation ${solicitation}`, size: FURN_SIZE, color: COLORS.muted })]
            : []),
        ],
      }),
    ],
  });
}

// Running footer: thin navy rule above; document/volume title (muted 8pt) left,
// section name center, "Page X of Y" right.
function createDocumentFooter(documentTitle: string, sectionName: string): Footer {
  const half = Math.round(CONTENT_WIDTH_DXA / 2);
  return new Footer({
    children: [
      new Paragraph({
        tabStops: [
          { type: TabStopType.CENTER, position: half },
          { type: TabStopType.RIGHT, position: CONTENT_WIDTH_DXA },
        ],
        border: { top: { style: BorderStyle.SINGLE, size: 6, color: COLORS.navy } },
        spacing: { before: 40 },
        children: [
          new TextRun({ text: documentTitle || '', size: 16, color: COLORS.muted }),
          new TextRun({ text: `\t${sectionName || ''}`, size: 16, color: COLORS.muted }),
          new TextRun({ text: '\tPage ', size: 16, color: COLORS.muted }),
          new TextRun({ children: [PageNumber.CURRENT], size: 16, color: COLORS.muted }),
          new TextRun({ text: ' of ', size: 16, color: COLORS.muted }),
          new TextRun({ children: [PageNumber.TOTAL_PAGES], size: 16, color: COLORS.muted }),
        ],
      }),
    ],
  });
}

function emptyHeader(): Header {
  return new Header({ children: [new Paragraph({ children: [] })] });
}

function emptyFooter(): Footer {
  return new Footer({ children: [new Paragraph({ children: [] })] });
}

// ============================================================================
// LOGO FETCH (DOCX embeds the image bytes; PDF uses <img> separately)
// ============================================================================

interface DocxLogo { data: ArrayBuffer; width: number; height: number; type: 'png' | 'jpg' | 'gif' | 'bmp'; }

function logoTypeFromUrl(url: string, mime?: string): DocxLogo['type'] {
  const m = (mime || '').toLowerCase();
  if (m.includes('png')) return 'png';
  if (m.includes('jpeg') || m.includes('jpg')) return 'jpg';
  if (m.includes('gif')) return 'gif';
  if (m.includes('bmp')) return 'bmp';
  const ext = (url.split('?')[0].split('.').pop() || '').toLowerCase();
  if (ext === 'png') return 'png';
  if (ext === 'jpg' || ext === 'jpeg') return 'jpg';
  if (ext === 'gif') return 'gif';
  if (ext === 'bmp') return 'bmp';
  return 'png';
}

/**
 * Fetch a logo for embedding. Returns null on ANY failure (CORS, 404, private
 * bucket) so the cover page degrades gracefully to a text wordmark.
 */
async function fetchDocxLogo(url: string): Promise<DocxLogo | null> {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    const data = await blob.arrayBuffer();

    const MAX_W = 170, MAX_H = 90;
    let width = MAX_W, height = MAX_H;
    try {
      // @ts-ignore createImageBitmap is widely supported in browsers
      const bmp = await createImageBitmap(blob);
      const scale = Math.min(MAX_W / bmp.width, MAX_H / bmp.height, 1);
      width = Math.max(1, Math.round(bmp.width * scale));
      height = Math.max(1, Math.round(bmp.height * scale));
      // @ts-ignore close exists on ImageBitmap
      bmp.close?.();
    } catch { /* keep defaults */ }

    return { data, width, height, type: logoTypeFromUrl(url, blob.type) };
  } catch {
    return null;
  }
}

// ============================================================================
// TABLE OF CONTENTS GENERATOR
// ============================================================================

function createTableOfContents(): (Paragraph | TableOfContents)[] {
  return [
    new Paragraph({
      children: [new TextRun({ text: 'TABLE OF CONTENTS', bold: true, size: 36, color: COLORS.primary })],
      spacing: { before: 400, after: 400 },
      border: {
        bottom: { style: BorderStyle.SINGLE, size: 12, color: COLORS.primary },
      },
    }),
    new TableOfContents('Table of Contents', {
      hyperlink: true,
      headingStyleRange: '1-3',
      stylesWithLevels: [
        new StyleLevel('Heading1', 1),
        new StyleLevel('Heading2', 2),
        new StyleLevel('Heading3', 3),
      ],
    }),
    new Paragraph({ children: [new PageBreak()] }),
  ];
}

// ============================================================================
// COVER PAGE (Word) — mirrors ExportCoverPage (PDF) + SerVet reference
// ============================================================================

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: 'auto' } as const;
const BORDERLESS_TABLE = {
  top: NO_BORDER, bottom: NO_BORDER, left: NO_BORDER, right: NO_BORDER,
  insideHorizontal: NO_BORDER, insideVertical: NO_BORDER,
};

function coverMetaTable(meta: CoverPageModel['meta']): Table {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: BORDERLESS_TABLE,
    rows: meta.map((r) =>
      new TableRow({
        children: [
          new TableCell({
            width: { size: 1900, type: WidthType.DXA },
            margins: { top: 20, bottom: 20, left: 0, right: 140 },
            children: [new Paragraph({ children: [new TextRun({ text: r.label.toUpperCase(), bold: true, size: 24, color: COLORS.navy })] })],
          }),
          new TableCell({
            margins: { top: 20, bottom: 20 },
            children: [new Paragraph({ children: [new TextRun({ text: r.value, size: 24, color: COLORS.ink })] })],
          }),
        ],
      })
    ),
  });
}

function submittedTable(model: CoverPageModel): Table {
  const col = (heading: string, lines: string[]) =>
    new TableCell({
      width: { size: 4680, type: WidthType.DXA },
      margins: { top: 20, bottom: 20, left: 0, right: 220 },
      children: [
        new Paragraph({
          children: [new TextRun({ text: heading.toUpperCase(), bold: true, size: 24, color: COLORS.navy })],
          spacing: { after: 60 },
        }),
        ...(lines.length ? lines : [TO_BE_COMPLETED]).map((l, i) =>
          new Paragraph({
            children: [new TextRun({ text: l, bold: i === 0, size: 24, color: COLORS.ink })],
            spacing: { after: 20 },
          })
        ),
      ],
    });

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: BORDERLESS_TABLE,
    rows: [new TableRow({ children: [col('Submitted To', model.submittedTo), col('Submitted By', model.submittedBy)] })],
  });
}

function buildDocxCoverPage(model: CoverPageModel, logo: DocxLogo | null): (Paragraph | Table)[] {
  const els: (Paragraph | Table)[] = [];

  // Wordmark / logo
  if (logo) {
    els.push(new Paragraph({
      children: [new ImageRun({ data: logo.data, type: logo.type, transformation: { width: logo.width, height: logo.height } })],
      spacing: { before: 500, after: 60 },
    }));
  } else {
    els.push(new Paragraph({
      children: [new TextRun({ text: model.wordmark || 'Offeror', bold: true, size: 56, color: COLORS.navy })],
      spacing: { before: 700, after: 40 },
    }));
  }
  if (model.tagline) {
    els.push(new Paragraph({
      children: [new TextRun({ text: model.tagline.toUpperCase(), bold: true, size: 24, color: COLORS.red })],
      spacing: { after: 60 },
    }));
  }

  // Navy + red rule pair
  els.push(createRule(COLORS.navy, { before: 40, after: 0, size: 14 }));
  els.push(createRule(COLORS.red, { before: 0, after: 480, size: 8 }));

  // Eyebrow / title / subtitle / volume
  els.push(new Paragraph({
    children: [new TextRun({ text: model.eyebrow, bold: true, size: 24, color: COLORS.muted })],
    spacing: { before: 160, after: 120 },
  }));
  els.push(new Paragraph({
    children: [new TextRun({ text: model.title, bold: true, size: 44, color: COLORS.navy })],
    spacing: { after: 80 },
  }));
  if (model.subtitle) {
    els.push(new Paragraph({
      children: [new TextRun({ text: model.subtitle, size: 26, color: COLORS.slate })],
      spacing: { after: 140 },
    }));
  }
  if (model.volumeLabel) {
    els.push(new Paragraph({
      children: [
        new TextRun({ text: 'Volume: ', bold: true, size: 24, color: COLORS.navy }),
        new TextRun({ text: model.volumeLabel, size: 24, color: COLORS.ink }),
      ],
      spacing: { after: 200 },
    }));
  }

  // Identity meta block
  els.push(coverMetaTable(model.meta));

  // Spacer toward the lower third
  els.push(new Paragraph({ children: [], spacing: { after: 700 } }));
  els.push(new Paragraph({ children: [], spacing: { after: 700 } }));

  // Submitted To / By
  els.push(createRule(COLORS.border, { before: 60, after: 140 }));
  els.push(submittedTable(model));

  // Confidentiality note
  els.push(new Paragraph({
    children: [new TextRun({ text: model.confidentialityNote, italics: true, size: 16, color: COLORS.muted })],
    spacing: { before: 300 },
  }));

  return els;
}

// Federal document styles (Times New Roman 12pt, navy headings).
const FED_DOC_STYLES = {
  default: {
    document: { run: { font: 'Times New Roman', size: 24 } },
    heading1: {
      run: { font: 'Times New Roman', size: 30, bold: true, color: COLORS.navy },
      paragraph: { spacing: { before: 240, after: 120 }, border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: COLORS.navy } } },
    },
    heading2: {
      run: { font: 'Times New Roman', size: 26, bold: true, color: COLORS.navy },
      paragraph: { spacing: { before: 240, after: 80 } },
    },
    heading3: {
      run: { font: 'Times New Roman', size: 24, bold: true, color: COLORS.slate },
      paragraph: { spacing: { before: 200, after: 60 } },
    },
  },
};

const PAGE_MARGIN_1IN = {
  top: convertInchesToTwip(1),
  right: convertInchesToTwip(1),
  bottom: convertInchesToTwip(1),
  left: convertInchesToTwip(1),
};

// The Pricing section's CLIN schedule / labor-rate buildup tables run 7-10
// columns wide — too wide for a 6.5in portrait page. That section gets its
// own landscape page(s). NOTE: the docx library swaps width/height itself
// when orientation is LANDSCAPE, so pass the PORTRAIT dimensions here.
const PAGE_SIZE_LETTER_LANDSCAPE = {
  ...PAGE_SIZE_LETTER,
  orientation: PageOrientation.LANDSCAPE,
};
const PAGE_MARGIN_LANDSCAPE = {
  top: convertInchesToTwip(0.75),
  right: convertInchesToTwip(0.75),
  bottom: convertInchesToTwip(0.75),
  left: convertInchesToTwip(0.75),
};
function isPricingSectionName(name: string): boolean {
  return name.toLowerCase().includes('pricing');
}

function sanitizeFileName(name: string, max = 50): string {
  return (name || 'proposal').replace(/[^a-z0-9]/gi, '_').substring(0, max);
}

// ============================================================================
// MAIN EXPORT FUNCTION
// ============================================================================

interface DocxBlock { sectionName: string; children: (Paragraph | Table)[]; }

export async function exportBeautifulDocx(
  proposal: ProposalContent,
  contract: Contract,
  identity?: ExportCompanyIdentity
): Promise<void> {
  resetSectionCounter();

  const parsedCl = parseContent(proposal.coverLetter);
  const id = identity ?? buildCompanyIdentity(null, parsedCl);
  const solicitation = resolveSolicitationNumber(contract, parsedCl);
  const docTitle = cleanTitle(contract.title);
  const coverModel = buildCoverPageModel(contract, id, { coverLetter: parsedCl });
  const logo = id.logoUrl ? await fetchDocxLogo(id.logoUrl) : null;

  // ---- Cover section children (cover page + TOC, no running furniture) ----
  const coverChildren: (Paragraph | Table | TableOfContents)[] = [
    ...buildDocxCoverPage(coverModel, logo),
    new Paragraph({ children: [new PageBreak()] }),
    ...createTableOfContents(),
  ];

  // ---- Content blocks (each becomes its own docx section) ----
  const blocks: DocxBlock[] = [];

  // Cover letter (its own letterhead; no numbered heading)
  if (proposal.coverLetter) {
    const cl = parseContent(proposal.coverLetter);
    const clChildren = cl && isCoverLetterContent(cl)
      ? exportCoverLetter(cl, id, proposal.demoLink)
      : exportFallbackText(proposal.coverLetter, 'Cover Letter');
    blocks.push({ sectionName: 'Cover Letter', children: clChildren });
  }

  // Technical sections
  const sectionMap: Record<string, (data: any) => (Paragraph | Table)[]> = {
    'Executive Summary': exportExecutiveSummary,
    'Technical Approach': exportTechnicalApproach,
    'Management Approach': exportManagementApproach,
    'Past Performance': exportPastPerformance,
    'Key Personnel': exportPersonnel,
    'Pricing & Cost Strategy': exportPricing,
    'FAR/DFARS Compliance': exportCompliance,
  };
  const typeGuards: Record<string, (data: any) => boolean> = {
    'Executive Summary': isExecutiveSummaryContent,
    'Technical Approach': isTechnicalApproachContent,
    'Management Approach': isManagementApproachContent,
    'Past Performance': isPastPerformanceContent,
    'Key Personnel': isPersonnelContent,
    'Pricing & Cost Strategy': isPricingContent,
    'FAR/DFARS Compliance': isComplianceContent,
  };

  for (const section of proposal.technicalSections) {
    const heading = createNumberedSectionHeading(section.title.toUpperCase(), 1);
    const body: (Paragraph | Table)[] = [];
    const sectionData = parseContent(section.content);
    const exporter = sectionMap[section.title];
    const typeGuard = typeGuards[section.title];
    try {
      if (sectionData && exporter && typeGuard && typeGuard(sectionData)) {
        body.push(...exporter(sectionData));
      } else {
        body.push(...exportFallbackText(section.content, section.title));
      }
    } catch (err) {
      console.error(`DOCX export: section "${section.title}" failed, using fallback`, err);
      try {
        body.push(...exportFallbackText(section.content, section.title));
      } catch {
        body.push(createParagraph('[This section could not be rendered.]'));
      }
    }
    blocks.push({ sectionName: section.title, children: [heading, ...body] });
  }

  // Deliverables
  const dd = proposal.deliverablesData;
  if (dd?.deliverables_with_labor && dd.deliverables_with_labor.length > 0) {
    const heading = createNumberedSectionHeading('DELIVERABLES ANALYSIS', 1);
    blocks.push({ sectionName: 'Deliverables Analysis', children: [heading, ...exportDeliverables(dd)] });
  }

  // Product / Solution
  if (dd?.product_definition) {
    const heading = createNumberedSectionHeading('PRODUCT/SOLUTION DEFINITION', 1);
    blocks.push({ sectionName: 'Product / Solution Definition', children: [heading, ...exportProductDefinition(dd.product_definition)] });
  }

  // ---- Assemble docx sections ----
  const docSections: any[] = [
    {
      properties: { page: { size: PAGE_SIZE_LETTER, margin: PAGE_MARGIN_1IN } },
      headers: { default: emptyHeader() },
      footers: { default: emptyFooter() },
      children: coverChildren,
    },
    ...blocks.map((b, i) => {
      const landscape = isPricingSectionName(b.sectionName);
      return {
        properties: {
          type: SectionType.NEXT_PAGE,
          page: {
            // Always set an explicit size: the docx library's default is A4,
            // which silently gave body pages a different paper size than the
            // Letter cover.
            size: landscape ? PAGE_SIZE_LETTER_LANDSCAPE : PAGE_SIZE_LETTER,
            margin: landscape ? PAGE_MARGIN_LANDSCAPE : PAGE_MARGIN_1IN,
            // Number body pages from 1 (the cover/TOC section carries no number).
            pageNumbers: i === 0 ? { start: 1 } : undefined,
          },
        },
        headers: { default: createDocumentHeader(id.companyName, solicitation) },
        footers: { default: createDocumentFooter(docTitle, b.sectionName) },
        children: b.children,
      };
    }),
  ];

  const doc = new Document({
    creator: 'BidWin Proposal Generator',
    title: `Proposal Response - ${contract.title}`,
    subject: `Proposal for ${contract.agency}`,
    description: `Proposal response for solicitation ${solicitation || 'N/A'}`,
    styles: FED_DOC_STYLES,
    numbering: BULLET_NUMBERING,
    sections: docSections,
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${sanitizeFileName(contract.title)}_proposal.docx`);
}

// ============================================================================
// STANDALONE COVER LETTER EXPORT
// ============================================================================

// Shared builder for a single-section ("standalone") submission-ready document:
// cover page (no furniture) + one content section with running header/footer.
async function buildStandaloneDoc(opts: {
  contract: Contract;
  identity: ExportCompanyIdentity;
  sectionName: string;
  blockChildren: (Paragraph | Table)[];
  volumeLabel?: string;
  coverLetter?: any;
}): Promise<Blob> {
  const { contract, identity, sectionName, blockChildren, coverLetter } = opts;
  const solicitation = resolveSolicitationNumber(contract, coverLetter);
  const docTitle = cleanTitle(contract.title);
  const coverModel = buildCoverPageModel(contract, identity, {
    volumeLabel: opts.volumeLabel ?? sectionName,
    coverLetter,
  });
  const logo = identity.logoUrl ? await fetchDocxLogo(identity.logoUrl) : null;
  const landscape = isPricingSectionName(sectionName);

  const doc = new Document({
    creator: 'BidWin Proposal Generator',
    title: `${sectionName} - ${contract.title}`,
    styles: FED_DOC_STYLES,
    numbering: BULLET_NUMBERING,
    sections: [
      {
        properties: { page: { size: PAGE_SIZE_LETTER, margin: PAGE_MARGIN_1IN } },
        headers: { default: emptyHeader() },
        footers: { default: emptyFooter() },
        children: buildDocxCoverPage(coverModel, logo),
      },
      {
        properties: {
          type: SectionType.NEXT_PAGE,
          page: {
            size: landscape ? PAGE_SIZE_LETTER_LANDSCAPE : PAGE_SIZE_LETTER,
            margin: landscape ? PAGE_MARGIN_LANDSCAPE : PAGE_MARGIN_1IN,
            pageNumbers: { start: 1 },
          },
        },
        headers: { default: createDocumentHeader(identity.companyName, solicitation) },
        footers: { default: createDocumentFooter(docTitle, sectionName) },
        children: blockChildren,
      },
    ],
  });

  return Packer.toBlob(doc);
}

async function exportCoverLetterDocx(
  coverLetterContent: string,
  contract: Contract,
  identity?: ExportCompanyIdentity
): Promise<void> {
  const cl = parseContent(coverLetterContent);
  const id = identity ?? buildCompanyIdentity(null, cl);
  const children = cl && isCoverLetterContent(cl)
    ? exportCoverLetter(cl, id)
    : exportFallbackText(coverLetterContent, 'Cover Letter');

  const blob = await buildStandaloneDoc({
    contract, identity: id, sectionName: 'Cover Letter', blockChildren: children, coverLetter: cl,
  });
  saveAs(blob, `${sanitizeFileName(contract.title)}_cover_letter.docx`);
}

// ============================================================================
// STANDALONE SECTION EXPORT
// ============================================================================

async function exportSectionDocx(
  sectionTitle: string,
  sectionContent: string,
  contract: Contract,
  identity?: ExportCompanyIdentity
): Promise<void> {
  resetSectionCounter();
  const id = identity ?? buildCompanyIdentity(null);
  const children: (Paragraph | Table)[] = [createNumberedSectionHeading(sectionTitle.toUpperCase(), 1)];

  const sectionData = parseContent(sectionContent);

  const sectionMap: Record<string, (data: any) => (Paragraph | Table)[]> = {
    'Executive Summary': exportExecutiveSummary,
    'Technical Approach': exportTechnicalApproach,
    'Management Approach': exportManagementApproach,
    'Past Performance': exportPastPerformance,
    'Key Personnel': exportPersonnel,
    'Pricing & Cost Strategy': exportPricing,
    'FAR/DFARS Compliance': exportCompliance,
  };
  const typeGuards: Record<string, (data: any) => boolean> = {
    'Executive Summary': isExecutiveSummaryContent,
    'Technical Approach': isTechnicalApproachContent,
    'Management Approach': isManagementApproachContent,
    'Past Performance': isPastPerformanceContent,
    'Key Personnel': isPersonnelContent,
    'Pricing & Cost Strategy': isPricingContent,
    'FAR/DFARS Compliance': isComplianceContent,
  };

  const exporter = sectionMap[sectionTitle];
  const typeGuard = typeGuards[sectionTitle];

  if (sectionData && exporter && typeGuard && typeGuard(sectionData)) {
    children.push(...exporter(sectionData));
  } else {
    children.push(...exportFallbackText(sectionContent, sectionTitle));
  }

  const blob = await buildStandaloneDoc({
    contract, identity: id, sectionName: sectionTitle, blockChildren: children,
  });
  const sanitizedSection = sectionTitle.replace(/[^a-z0-9]/gi, '_').substring(0, 30);
  saveAs(blob, `${sanitizeFileName(contract.title, 30)}_${sanitizedSection}.docx`);
}

// ============================================================================
// STANDALONE DELIVERABLES EXPORT
// ============================================================================

async function exportDeliverablesDocx(
  deliverablesData: DeliverablesData,
  contract: Contract,
  identity?: ExportCompanyIdentity
): Promise<void> {
  resetSectionCounter();
  const id = identity ?? buildCompanyIdentity(null);
  const children: (Paragraph | Table)[] = [
    createNumberedSectionHeading('DELIVERABLES ANALYSIS', 1),
    ...exportDeliverables(deliverablesData),
  ];

  const blob = await buildStandaloneDoc({
    contract, identity: id, sectionName: 'Deliverables Analysis', blockChildren: children,
  });
  saveAs(blob, `${sanitizeFileName(contract.title, 30)}_deliverables.docx`);
}

// ============================================================================
// STANDALONE PRODUCT DEFINITION EXPORT
// ============================================================================

async function exportProductDocx(
  productDefinition: ProductDefinition,
  contract: Contract,
  identity?: ExportCompanyIdentity
): Promise<void> {
  resetSectionCounter();
  const id = identity ?? buildCompanyIdentity(null);
  const children: (Paragraph | Table)[] = [
    createNumberedSectionHeading('PRODUCT/SOLUTION DEFINITION', 1),
    ...exportProductDefinition(productDefinition),
  ];

  const blob = await buildStandaloneDoc({
    contract, identity: id, sectionName: 'Product / Solution Definition', blockChildren: children,
  });
  saveAs(blob, `${sanitizeFileName(contract.title, 30)}_product_solution.docx`);
}

// ============================================================================
// EXPORT CLASS (for backwards compatibility + new methods)
// ============================================================================

export class BeautifulDocxExporter {
  /** Export complete proposal to a submission-ready Word document. */
  static async export(proposal: ProposalContent, contract: Contract, identity?: ExportCompanyIdentity): Promise<void> {
    return exportBeautifulDocx(proposal, contract, identity);
  }

  /** Export cover letter as a separate Word document. */
  static async exportCoverLetterOnly(coverLetterContent: string, contract: Contract, identity?: ExportCompanyIdentity): Promise<void> {
    return exportCoverLetterDocx(coverLetterContent, contract, identity);
  }

  /** Export a single section as a Word document. */
  static async exportSection(sectionTitle: string, sectionContent: string, contract: Contract, identity?: ExportCompanyIdentity): Promise<void> {
    return exportSectionDocx(sectionTitle, sectionContent, contract, identity);
  }

  /** Export deliverables analysis as a Word document. */
  static async exportDeliverablesOnly(deliverablesData: DeliverablesData, contract: Contract, identity?: ExportCompanyIdentity): Promise<void> {
    return exportDeliverablesDocx(deliverablesData, contract, identity);
  }

  /** Export product/solution definition as a Word document. */
  static async exportProductOnly(productDefinition: ProductDefinition, contract: Contract, identity?: ExportCompanyIdentity): Promise<void> {
    return exportProductDocx(productDefinition, contract, identity);
  }
}
